/**
 * ListingForm — Multi-step form for sellers to create or edit a house listing.
 * Steps:
 *   Property Details (home facts, then price + AI price estimate) →
 *     [Listing Fee (billing on only)] → Confirm Ownership →
 *     Description & Photos → [WI Seller Disclosure (WI only)] →
 *     Sign Listing Agreement → Review & Publish
 * Props:
 *   - editHouse: existing house object to edit (omit for create mode)
 */

import React, { useState, useRef, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import APIAgent from '../../agents/apiAgent';
import { compressImage } from '../../utils/imageCompression';
import { resolveDownloadUrl } from '../../utils/downloadUrl';
import {
  MLS_SECTIONS,
  applyPaperworkAnswer,
  createInitialMlsProfile,
  isMlsFieldRequired,
  missingMlsFields,
  normalizeMlsProfile,
  toLabel,
  toggleMultiselectOption,
} from './mlsFieldSchema';
import PriceEstimateCard, { formatPrice } from '../PriceEstimate/PriceEstimateCard';
import RoomsEditor, { getRoomsPublishErrors } from './RoomsEditor';
import BathBreakdownEditor, { decomposeLegacyBaths } from './BathBreakdownEditor';
import RecrDisclosureForm from './RecrDisclosureForm';
import PropertyTypeFields from './PropertyTypeFields';
import PublishChecklist from './PublishChecklist';
import {
  CATEGORY_RESIDENTIAL,
  CATEGORY_VACANT_LAND,
  DISCLOSURE_BASIS_STATUTORY,
  DISCLOSURE_BASIS_VOLUNTARY,
  LISTING_QUALIFIER_KEYS,
  STATUS_OK,
  deriveFromProfile,
  isBlocked,
  paperworkKey,
} from './paperworkCategory';
import { copyText } from './paperworkCopy';
import {
  DISCLOSURE_TITLE_RECR,
  GENERIC_BLOCK_MESSAGE,
  UPLOAD_WI_CONDO_RECR_ADDENDUM,
  disclosureSigningText,
  disclosureTitleFor,
  paperworkLine,
  requiredUploadsFor,
  resultMessage,
} from './paperworkPresentation';
import {
  CONDITION_REPORT_REQUIRED,
  PAPERWORK_BLOCKED,
  PAPERWORK_CATEGORY_LOCKED,
  PAPERWORK_CATEGORY_REQUIRED,
  PROPERTY_FEATURES_INCOMPLETE,
  REQUIRED_UPLOAD_MISSING,
  ADDRESS_SIGNING_LOCKED_MESSAGE,
  isAddressChangeRequiresWithdrawal,
  isConditionReportSigning,
  isDisclosureComplete,
  isDisclosureSigning,
  isListingAgreementRequired,
  isListingUnderContract,
  isOwnershipVerificationRequired,
  isStateLock,
  listingUnderContractMessage,
  paperworkErrorCode,
  paperworkLockMessage,
  publishRefusalCode,
  refusalMessage,
} from './publishGate';
import { isUploadComplete, uploadsFromPaperwork } from '../../agents/requiredUploadsAgent';
import { DisclosureAPI } from '../../agents/disclosureAgent';
import { billingAgent } from '../../agents/billingAgent';
import { ownershipVerificationAgent } from '../../agents/ownershipVerificationAgent';
import {
  ADVANCEABLE_STATUSES,
  STATUS_LABELS as OWNERSHIP_STATUS_LABELS,
  isVerifiedStatus,
} from '../../utils/ownershipVerification';
import OwnershipVerificationStep from './OwnershipVerificationStep';
import ListingFeeStep from './ListingFeeStep';
import ListingAgreementPreviewButton from './ListingAgreementPreviewButton';
import { MLS_DATA_DISCLAIMER } from '../../utils/disclaimers';
import './ListingForm.css';

// Lazy-loaded so @stripe/react-stripe-js + Stripe.js only load when a seller
// actually reaches the Full Service payment step at publish.
const OneTimeChargeModal = lazy(() => import('../Billing/OneTimeChargeModal'));

/**
 * Detect the Full Service payment-required gate on a publish attempt.
 *
 * The status endpoint returns 402 with detail.code ===
 * 'full_service_payment_required' ONLY when billing is enabled (dark by default
 * — see houses.py). Matching on it is inert when billing is off: no 402 ever
 * fires, so the publish flow is unchanged. APIAgent serializes the detail object
 * into err.message.
 */
function isFullServicePaymentRequired(err) {
  if (!err || (err.status ?? err.statusCode) !== 402) return false;
  const msg = typeof err.message === 'string' ? err.message : '';
  return (
    msg.includes('full_service_payment_required') ||
    msg.toLowerCase().includes('full service payment required')
  );
}

const PROPERTY_STEP = 'Property Details';
// How often the wizard re-checks an ownership review while it waits (QA F5).
const OWNERSHIP_REVIEW_POLL_MS = 30000;
// The AI price estimate takes ~20–25s (the server takes the median of parallel
// AI samples) and occasionally ~35s; 30s cut off real answers.
const PRICE_ESTIMATE_TIMEOUT_MS = 60000;
const LISTING_FEE_STEP = 'Listing Fee';
const OWNERSHIP_STEP = 'Confirm Ownership';
const WI_DISCLOSURE_STEP = 'Seller Disclosure';
const DESCRIPTION_STEP = 'Description & Photos';
const SIGN_AGREEMENT_STEP = 'Sign Listing Agreement';
const REVIEW_STEP = 'Review & Publish';
// The AI price estimate is deliberately NOT a step of its own: it is an
// optional assist that belongs next to the Price field, not a gate the seller
// has to walk past to reach the rest of the wizard (QA T-31). Price + estimate
// close out Property Details, BELOW every home fact, because the estimate
// prices whatever has been filled in by the time the seller clicks it.
//
// Listing Fee exists only when billing is enabled, and sits right after
// Property Details so the tier (set by the price) is known. Confirm Ownership
// follows it: both need a saved draft, and the fee is refunded in full if
// ownership is rejected. Property Details is index 0 in every variant, so the
// billing config can load after mount without moving the first step.
// For WI, the disclosure step goes before Sign Listing Agreement.
const getSteps = (state, billingEnabled = false) => [
  PROPERTY_STEP,
  ...(billingEnabled ? [LISTING_FEE_STEP] : []),
  OWNERSHIP_STEP,
  DESCRIPTION_STEP,
  ...((state || '').toUpperCase() === 'WI' ? [WI_DISCLOSURE_STEP] : []),
  SIGN_AGREEMENT_STEP,
  REVIEW_STEP,
];

// Listing statuses whose address can still be edited. Anything else (live,
// pending, sold) must be withdrawn first — the server 409s
// `address_change_requires_withdrawal` — because the ownership documents were
// reviewed for the address as it stands.
const ADDRESS_EDITABLE_STATUSES = ['draft', 'withdrawn'];
export const ADDRESS_LOCKED_MESSAGE =
  "Withdraw the listing to change its address. You'll need to confirm ownership again before republishing.";

// `initialStep` values accepted from resume paths → the step they open.
// 'disclosure': back from signing the condition report in DocuSign.
const INITIAL_STEP_LABELS = {
  review: REVIEW_STEP,
  ownership: OWNERSHIP_STEP,
  disclosure: WI_DISCLOSURE_STEP,
  features: PROPERTY_STEP,
};

// 409 property_features_incomplete with no message of its own.
const PROPERTY_FEATURES_INCOMPLETE_FALLBACK =
  'Fill in every Property Features question before publishing this listing. Choose "None" where nothing applies.';

// Fallback disclaimers per legal requirements §5 if the backend preview omits
// them. Server-supplied list always takes precedence.
const FALLBACK_DISCLAIMERS = [
  'cutthe6.com is not a real estate broker, agent, attorney, or lender. We provide software and document tools only — we do not represent you in the sale.',
  'This is a binding legal contract. Read it before signing. If you have questions, consult a licensed real estate attorney in your state.',
  'We do not hold or move funds. Earnest money and closing funds are handled by a neutral, licensed escrow / title agent or closing attorney.',
  'You remain responsible for required disclosures and fair-housing compliance.',
  'By signing, you consent to use electronic records and signatures under the ESIGN Act and applicable state law. A paper copy may be requested at any time.',
  'Our fee is a flat service fee and is not contingent on the sale of your property.',
  'General educational information only — not legal advice. Consult a licensed real estate attorney in your jurisdiction.',
];

// ESIGN consent copy per docs/legal/seller-listing-agreement-requirements.md §3.
// Adapted for DocuSign embedded signing: the cryptographic signature is applied
// in DocuSign, but the consent acknowledgement stays on a screen we control.
const ESIGN_CONSENT_COPY =
  'I have read this Agreement and the disclosures above, and I agree to sign it electronically via DocuSign. I consent to the use of electronic records and signatures under the ESIGN Act / UETA. I understand I may request a paper copy or withdraw my consent for future electronic delivery.';

// ── Paperwork (docs/architecture/paperwork-categories.md §9) ─────────────
// The mls_profile keys the paperwork category is derived from.
const PAPERWORK_PROFILE_KEYS = ['property_type', ...LISTING_QUALIFIER_KEYS];

// What the server treats a listing saved before the paperwork questions as
// (design §3.5: stored NULL is residential / statutory).
const LEGACY_PAPERWORK_KEY = `${CATEGORY_RESIDENTIAL}:${DISCLOSURE_BASIS_STATUTORY}`;

// The seller-facing label, help and attestation for each required upload. The
// addendum is the interim § 709.02(2) upload (legal copy §7.2 option (ii), user
// decision), with legal's L4 wording (copy file §10, `WI.condo_addendum.*`).
// The seller's attestation is required (review §9.3). The server's
// `attestation_text` / `attestation_version` win over these when it sends them.
const REQUIRED_UPLOAD_COPY = {
  [UPLOAD_WI_CONDO_RECR_ADDENDUM]: {
    label: copyText('WI.condo_addendum.title'),
    help: copyText('WI.condo_addendum.help'),
    missing: copyText('WI.condo_addendum.missing'),
    attestationRequired: true,
    attestationText: copyText('WI.condo_addendum.attestation'),
    attestationVersion: copyText('WI.condo_addendum.attestation.version'),
  },
};

/**
 * `profile` with its paperwork facts replaced by `savedProfile`'s. Used when a
 * background save must not change the listing's paperwork: a hard block is
 * never persisted, and a category change after the report was signed voids
 * the report, which needs the seller's say-so first.
 */
const withSavedPaperworkFacts = (profile, savedProfile) => {
  const next = { ...(profile || {}) };
  PAPERWORK_PROFILE_KEYS.forEach((key) => {
    const saved = savedProfile?.[key];
    if (saved === undefined || saved === null || `${saved}`.trim() === '') delete next[key];
    else next[key] = saved;
  });
  return next;
};

/** Error thrown when the seller declines a save that would void their report. */
const saveCancelled = () => {
  const err = new Error('Save cancelled');
  err.cancelled = true;
  return err;
};

const formatUtcDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
};

const formatUtcShortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

const truncateHash = (hash) => {
  if (!hash || typeof hash !== 'string') return '';
  if (hash.length <= 12) return hash;
  return `${hash.slice(0, 4)}…${hash.slice(-4)}`;
};

const INITIAL_FORM = {
  address: '',
  city: '',
  state: '',
  zip_code: '',
  price: '',
  bedrooms: '',
  // bathrooms is now derived from the breakdown (full + 3/4 + 1/2) — no
  // longer a direct input on the wizard. Server still returns it on GET.
  square_feet: '',
  description: '',
  mls_profile: createInitialMlsProfile(),
  // New MLS-grade-listing-data fields:
  full_baths: 0,
  three_quarter_baths: 0,
  half_baths: 0,
  bath_zero_confirmed: false,
  rooms: [],
};

const hydratePriceEstimateFromMls = (mlsProfile) => {
  if (!mlsProfile || typeof mlsProfile !== 'object') return null;

  const saved = mlsProfile.ai_price_estimate;
  if (!saved || typeof saved !== 'object') return null;

  const low = Number(saved.low);
  const recommended = Number(saved.recommended);
  const high = Number(saved.high);

  if ([low, recommended, high].some((val) => Number.isNaN(val))) {
    return null;
  }

  return {
    id: saved.id || null,
    estimate: {
      low,
      recommended,
      high,
    },
    confidence: saved.confidence || null,
    generated_at: saved.generated_at || null,
    disclaimer: saved.disclaimer || null,
    factors: Array.isArray(saved.factors) ? saved.factors : [],
    comparables: Array.isArray(mlsProfile.ai_comparable_properties)
      ? mlsProfile.ai_comparable_properties
      : [],
    // Fingerprint of the inputs it was generated from (null on estimates saved
    // before fingerprints existed — those are never flagged stale).
    inputSignature: typeof saved.input_signature === 'string' ? saved.input_signature : null,
  };
};

/**
 * The body POSTed to /api/agents/listing-price-estimate for the current form.
 *
 * Single source for both the request and its fingerprint, so "the estimate is
 * stale" means exactly "the estimate endpoint would now be sent something
 * different". Never carries `price` (the contract has no price field, so a
 * pre-price estimate is not circular) nor the `ai_*` keys a saved estimate
 * leaves in mls_profile — echoing old AI comparables back at the model is noise.
 */
const buildEstimateRequest = (form) => {
  const cleanProfile = {};
  Object.entries(form.mls_profile || {}).forEach(([key, val]) => {
    if (key.startsWith('ai_')) return;
    if (Array.isArray(val) && val.length > 0) {
      cleanProfile[key] = val;
    } else if (typeof val === 'string' && val.trim()) {
      cleanProfile[key] = val.trim();
    } else if (typeof val === 'number' && val) {
      cleanProfile[key] = val;
    }
  });

  // Derived bath count from the breakdown — server is the single source of
  // truth for the stored value.
  const derivedBaths =
    (form.full_baths || 0) * 1 +
    (form.three_quarter_baths || 0) * 0.75 +
    (form.half_baths || 0) * 0.5;
  // The breakdown goes too: "2.75" alone reads as a room count (QA T-37);
  // "2 full, 1 three-quarter, 1 half" doesn't.
  const hasBathBreakdown = derivedBaths > 0;

  return {
    address: form.address.trim(),
    city: form.city.trim(),
    state: form.state.toUpperCase().trim(),
    zip_code: form.zip_code.trim(),
    bedrooms: form.bedrooms ? parseInt(form.bedrooms, 10) : undefined,
    bathrooms: derivedBaths > 0 ? derivedBaths : undefined,
    full_baths: hasBathBreakdown ? form.full_baths || 0 : undefined,
    three_quarter_baths: hasBathBreakdown ? form.three_quarter_baths || 0 : undefined,
    half_baths: hasBathBreakdown ? form.half_baths || 0 : undefined,
    square_feet: form.square_feet ? parseFloat(form.square_feet) : undefined,
    year_built: cleanProfile.year_built ? parseInt(cleanProfile.year_built, 10) : undefined,
    property_type: cleanProfile.property_type || undefined,
    mls_profile: Object.keys(cleanProfile).length > 0 ? cleanProfile : undefined,
    rooms: buildEstimateRooms(form.rooms),
  };
};

// Mirrors EstimateRoom's limits in agent_endpoints.py, so a half-typed or
// out-of-range row degrades to "no dimensions" instead of a 422 that sinks the
// whole estimate.
const MAX_ESTIMATE_ROOMS = 40;
const MAX_ROOM_DIMENSION_FT = 500;

/**
 * The room inventory as the estimate endpoint takes it: rows with a type only
 * (a row still being filled in has none yet), without the editor's synthetic
 * ids, dimensions only when they're real numbers in range.
 */
const buildEstimateRooms = (rooms) => {
  const sized = (value) => {
    const n = parseFloat(value);
    return n > 0 && n <= MAX_ROOM_DIMENSION_FT ? n : undefined;
  };
  const list = (rooms || [])
    .filter((r) => r && r.room_type)
    .slice(0, MAX_ESTIMATE_ROOMS)
    .map((r) => ({
      room_type: r.room_type,
      level: r.level || undefined,
      length_ft: sized(r.length_ft),
      width_ft: sized(r.width_ft),
      flooring: r.flooring || undefined,
    }));
  return list.length > 0 ? list : undefined;
};

/** Key-order- and number/string-insensitive serialization for fingerprinting. */
const stableStringify = (value) => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }
  // "1968" typed into a field and 1968 hydrated from the server are the same input.
  return JSON.stringify(value == null ? null : String(value));
};

/** Short, stable fingerprint of an estimate request (djb2). */
const fingerprintEstimateRequest = (request) => {
  const text = stableStringify(request);
  let hash = 5381;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
};

// The details that move an estimate most. The estimate still runs without them
// (it's optional — QA T-31), but the panel names the missing ones: a sparse
// request prices a generic house in the ZIP, not this one.
const ESTIMATE_KEY_DETAILS = [
  { label: 'bedrooms', isSet: (f) => Boolean(f.bedrooms) },
  {
    label: 'bathrooms',
    isSet: (f) => (f.full_baths || 0) + (f.three_quarter_baths || 0) + (f.half_baths || 0) > 0,
  },
  { label: 'square feet', isSet: (f) => Boolean(f.square_feet) },
  { label: 'property type', isSet: (f) => Boolean(f.mls_profile?.property_type) },
  { label: 'year built', isSet: (f) => Boolean(f.mls_profile?.year_built) },
  {
    label: 'lot size',
    isSet: (f) => Boolean(f.mls_profile?.lot_size_sqft || f.mls_profile?.lot_size_acres),
  },
];

/** "a", "a and b", "a, b and c" */
const joinWithAnd = (items) =>
  items.length <= 1
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

export default function ListingForm({
  sellerId,
  onListingCreated,
  onListingSaved,
  onCancel,
  editHouse,
  // 'review' opens an existing listing straight at Review & Publish (the
  // DocuSign return's "Review & publish"). Omitted → Property Details.
  initialStep = null,
  // Create mode only: { address, city, state, zip_code } typed on the public
  // "start your listing" page. Applied once; onInitialValuesUsed is then called.
  initialValues = null,
  onInitialValuesUsed,
}) {
  const isEditMode = Boolean(editHouse);
  // Live/pending/sold listings can't change address in place (see above).
  const addressLocked =
    isEditMode && !ADDRESS_EDITABLE_STATUSES.includes(editHouse?.listing_status || 'draft');
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => (
    !editHouse && initialValues
      ? {
          ...INITIAL_FORM,
          address: initialValues.address || '',
          city: initialValues.city || '',
          state: initialValues.state || '',
          zip_code: initialValues.zip_code || '',
        }
      : INITIAL_FORM
  ));
  // Tell the parent the prefill was applied, so it isn't applied again.
  const initialValuesUsedRef = useRef(false);
  useEffect(() => {
    if (editHouse || !initialValues || initialValuesUsedRef.current) return;
    initialValuesUsedRef.current = true;
    if (onInitialValuesUsed) onInitialValuesUsed();
  }, [editHouse, initialValues, onInitialValuesUsed]);

  // ── Billing config (adds the Listing Fee step) ─────────────────────────
  // Treated as disabled until loaded. Property Details is index 0 either way;
  // if billing turns out to be on after the seller (or a resume path) already
  // moved past it, shift the index so they stay on the same step.
  const [billingEnabled, setBillingEnabled] = useState(false);
  // The Listing Fee step's last fee response: { billing_enabled,
  // tier_amount_cents, paid_cents, amount_due_cents }.
  const [listingFee, setListingFee] = useState(null);

  // The step whose entry save (saveDraftForStep) has finished. Steps that attach to the
  // draft only get the house id once their own entry save is done, so they
  // never read a stale server copy (e.g. the fee for the old price) — not even
  // for the one render between arriving on the step and the save starting.
  const [stepSaveDone, setStepSaveDone] = useState(null);
  useEffect(() => {
    setStepSaveDone(null);
    // A fee from an earlier visit may be for another price: refetch it.
    if (step === listingFeeStepIndex) setListingFee(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  // A price change can move the listing into another fee tier.
  useEffect(() => {
    setListingFee(null);
  }, [form.price]);
  const draftReadyForStep = stepSaveDone === step;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await billingAgent.getConfig();
        if (cancelled || config?.billing_enabled !== true) return;
        setBillingEnabled(true);
        setStep((s) => (s > 0 ? s + 1 : s));
      } catch (err) {
        // Unknown → behave as billing-off; the server's 402 still guards publish.
        console.warn('Billing config lookup failed; assuming billing is off:', err?.status);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Ownership verification ─────────────────────────────────────────────
  // VerificationStatus from GET /houses/{id}/ownership-verification. `null`
  // until loaded (or when there is no house id yet).
  const [ownershipStatus, setOwnershipStatus] = useState(null);
  const [ownershipLoading, setOwnershipLoading] = useState(false);
  const [ownershipError, setOwnershipError] = useState(null);
  // WI sellers now complete the actual Real Estate Condition Report here rather
  // than acknowledging that they will complete one later. `null` = not loaded.
  const [wiDisclosureStatus, setWiDisclosureStatus] = useState(null);
  // `signing` (out for the seller's DocuSign signature) is not complete.
  const wiDisclosureCompleted = isDisclosureComplete(wiDisclosureStatus);
  const wiDisclosureSigning = isDisclosureSigning(wiDisclosureStatus);
  // GET /disclosure `last_signing_error`: why an earlier DocuSign signature
  // didn't count (e.g. the date changed while signing). Null when absent.
  const [disclosureSigningError, setDisclosureSigningError] = useState(null);
  // The server's message for a save refused with 409 condition_report_signing
  // (the address changed while the report is out for signature).
  const [addressSigningError, setAddressSigningError] = useState(null);

  // ── Paperwork ──────────────────────────────────────────────────────────
  // The mls_profile last persisted (the server derives the category from it),
  // and the server's `paperwork` summary of the listing (§8.1), when known.
  const [savedProfile, setSavedProfile] = useState(editHouse?.mls_profile || null);
  const [serverPaperwork, setServerPaperwork] = useState(editHouse?.paperwork || null);
  // The server's message for a refused save (422 paperwork_blocked, 409
  // paperwork_category_locked) — reviewed copy for the listing's state.
  const [paperworkError, setPaperworkError] = useState(null);
  // A publish refusal the seller must act on (condition_report_required, …).
  const [publishRefusal, setPublishRefusal] = useState(null);
  // Changing the category after the report was signed voids it (legal §4).
  // Set once the refreshed status confirms the old report no longer counts.
  const [disclosureVoided, setDisclosureVoided] = useState(false);
  const voidPendingRef = useRef(false);
  // Bumped to re-read the disclosure status after a paperwork change.
  const [disclosureStatusNonce, setDisclosureStatusNonce] = useState(0);
  // code → {uploaded, originalFilename, uploadedAt} uploaded this session.
  const [sessionUploads, setSessionUploads] = useState({});

  // Imperative handle for the RoomsEditor — lets the wizard query publish-time
  // validation without lifting RoomsEditor's whole state up.
  const roomsEditorRef = useRef(null);
  // Hard validation only fires on a publish attempt; while editing, partial
  // rows stay tolerant per PRD §7.
  const [showRoomErrors, setShowRoomErrors] = useState(false);

  // ── Listing-agreement signing state ────────────────────────────────────
  // The house id we'll bind the signed agreement to. In create mode this is
  // populated when we silently save a draft on entering the sign step.
  const [signingHouseId, setSigningHouseId] = useState(editHouse?.id ?? null);
  // Server preview payload: { template_id, template_version, template_sha256,
  // rendered_html, consent_version, disclaimers }
  const [agreementPreview, setAgreementPreview] = useState(null);
  const [agreementLoading, setAgreementLoading] = useState(false);
  const [agreementError, setAgreementError] = useState(null);
  // Persistent signed-agreement record from GET /houses/{id}/listing-agreement:
  // { has_signed_agreement, document_id, template_version, signed_at,
  //   download_url, current_template_version, signer_name? }
  const [signedAgreement, setSignedAgreement] = useState(null);
  // Pre-sign affirmation: ESIGN consent + scroll gate. With DocuSign embedded
  // signing the legal name is captured inside DocuSign, so there is no longer a
  // typed-name field on this screen.
  const [consentChecked, setConsentChecked] = useState(false);
  const [previewScrolledToBottom, setPreviewScrolledToBottom] = useState(false);
  const [signSubmitting, setSignSubmitting] = useState(false);
  const [signError, setSignError] = useState(null);
  // Toggle to re-expand the agreement preview after signing
  const [showSignedAgreement, setShowSignedAgreement] = useState(false);
  // a11y live-region message
  const [signedAnnouncement, setSignedAnnouncement] = useState('');
  // Race-condition guard: 409 on publish surfaces as a red toast
  const [publishGateError, setPublishGateError] = useState(null);
  // Full Service charge (PR5): true once a publish attempt hit the 402 payment
  // gate, so the Stripe charge modal opens. Stays false when billing is disabled
  // (the 402 never fires), keeping the publish flow unchanged.
  const [chargeOpen, setChargeOpen] = useState(false);
  // The house id the publish/charge applies to — set when persistListing flips
  // the status (so create-mode's freshly-created id is charged, not a stale one).
  const publishHouseIdRef = useRef(null);

  // Refs used by the sign step
  const previewBottomSentinelRef = useRef(null);
  const previewScrollContainerRef = useRef(null);
  const signedConfirmRef = useRef(null);
  // New photos the seller has added this session. Each item:
  //   { id, file, preview, processing }
  // `file` starts as the original and is swapped for a compressed version once
  // background compression finishes; `processing` drives the per-thumbnail
  // spinner. `existingImageUrls` holds already-uploaded photos (edit mode).
  const [newPhotos, setNewPhotos] = useState([]);
  const [existingImageUrls, setExistingImageUrls] = useState([]);
  const photoIdRef = useRef(0);
  // Ids of new photos already uploaded to the draft this session. The step
  // saves below can run several times, and a server response without
  // `image_urls` keeps the local previews around — never upload those twice.
  const uploadedPhotoIdsRef = useRef(new Set());

  // Tracks whether the editor pre-filled the bath breakdown from a legacy
  // `bathrooms` float. Surfaces the "Estimated breakdown" badge until the
  // seller touches a stepper.
  const [bathWasEstimated, setBathWasEstimated] = useState(false);

  useEffect(() => {
    if (editHouse) {
      const mergedMlsProfile = normalizeMlsProfile({
        ...createInitialMlsProfile(),
        ...(editHouse.mls_profile || {}),
      });

      // Bath breakdown hydration — server is the source of truth for the
      // three component ints, but falls back to a one-time decomposition
      // of the legacy `bathrooms` float for listings that pre-date the
      // breakdown columns. The "estimated" badge clears on first edit.
      const hasBreakdown =
        editHouse.full_baths != null ||
        editHouse.three_quarter_baths != null ||
        editHouse.half_baths != null;
      let nextFull = editHouse.full_baths || 0;
      let nextThree = editHouse.three_quarter_baths || 0;
      let nextHalf = editHouse.half_baths || 0;
      let wasEstimated = false;
      if (!hasBreakdown && typeof editHouse.bathrooms === 'number') {
        const decomposed = decomposeLegacyBaths(editHouse.bathrooms);
        nextFull = decomposed.full;
        nextThree = decomposed.threeQuarter;
        nextHalf = decomposed.half;
        wasEstimated = decomposed.full + decomposed.threeQuarter + decomposed.half > 0;
      }
      setBathWasEstimated(wasEstimated);

      setForm({
        address: editHouse.address || '',
        city: editHouse.city || '',
        state: editHouse.state || '',
        zip_code: editHouse.zip_code || '',
        price: editHouse.price != null ? String(editHouse.price) : '',
        bedrooms: editHouse.bedrooms != null ? String(editHouse.bedrooms) : '',
        square_feet: editHouse.square_feet != null ? String(editHouse.square_feet) : '',
        description: editHouse.description || '',
        mls_profile: mergedMlsProfile,
        full_baths: nextFull,
        three_quarter_baths: nextThree,
        half_baths: nextHalf,
        bath_zero_confirmed: false,
        rooms: Array.isArray(editHouse.rooms)
          ? editHouse.rooms.map((r) => ({
              id: r.id,
              room_type: r.room_type || '',
              level: r.level || '',
              length_ft: r.length_ft != null ? String(r.length_ft) : '',
              width_ft: r.width_ft != null ? String(r.width_ft) : '',
              flooring: r.flooring || '',
              features: r.features || '',
            }))
          : [],
      });

      setPriceEstimate(hydratePriceEstimateFromMls(editHouse.mls_profile));
      setPriceEstimateError(null);
      setPriceEstimateLoading(false);
      setSavedProfile(editHouse.mls_profile || null);
      setServerPaperwork(editHouse.paperwork || null);

      if (editHouse.image_urls?.length) {
        setExistingImageUrls(editHouse.image_urls);
      }
    } else {
      setPriceEstimate(null);
      setPriceEstimateError(null);
      setPriceEstimateLoading(false);
      setBathWasEstimated(false);
    }
  }, [editHouse]);

  // Jump to Review & Publish (or Confirm Ownership) once per listing when
  // asked. The step list comes from the listing's state (Wisconsin adds a
  // step) and billing (adds Listing Fee), so index it from there; a billing
  // config that lands later shifts the index (see above). Every publish rule
  // is still enforced there by canPublish, and Back walks the earlier steps.
  const initialStepAppliedFor = useRef(null);
  useEffect(() => {
    // 'paperwork': a 409 paperwork_category_required (e.g. from Negotiate)
    // sent the seller here. Property Details, at the property-type questions.
    const toPaperwork = initialStep === 'paperwork';
    const target = toPaperwork ? PROPERTY_STEP : INITIAL_STEP_LABELS[initialStep];
    if (!editHouse || !target) return;
    if (initialStepAppliedFor.current === editHouse.id) return;
    initialStepAppliedFor.current = editHouse.id;
    // A step this listing doesn't have (the disclosure step outside
    // Wisconsin) leaves the wizard where it opened.
    const targetIndex = getSteps(editHouse.state || '', billingEnabled).indexOf(target);
    if (targetIndex >= 0) setStep(targetIndex);
    if (toPaperwork) {
      const frame = typeof window !== 'undefined' && window.requestAnimationFrame
        ? window.requestAnimationFrame
        : (fn) => setTimeout(fn, 0);
      frame(() => {
        const block = document.querySelector('[data-testid="property-type-block"]');
        if (block && typeof block.scrollIntoView === 'function') {
          block.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
    // billingEnabled deliberately omitted: this applies once per listing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editHouse, initialStep]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Price estimate state
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [priceEstimateLoading, setPriceEstimateLoading] = useState(false);
  const [priceEstimateError, setPriceEstimateError] = useState(null);
  // False when the backend reports a terminal misconfiguration (HTTP 502) —
  // suppresses the retry affordance so we don't tell the user to try again
  // when trying again cannot possibly work.
  const [priceEstimateRetryable, setPriceEstimateRetryable] = useState(true);

  const hasAnyBath =
    (form.full_baths || 0) + (form.three_quarter_baths || 0) + (form.half_baths || 0) > 0;
  // A listing without an asking price shows buyers "$N/A" and seeds the offer
  // form at $0, so price is required to leave Property Details and to publish.
  // Save as Draft still works without one.
  const hasListingPrice = Number(form.price) > 0;
  // Vacant land has no bedrooms, baths or living area (those fields are hidden
  // there); the estimate works from the lot size instead.
  const landListing = deriveFromProfile(form.mls_profile)?.category === CATEGORY_VACANT_LAND;
  const canGetEstimate =
    form.address.length >= 5 &&
    form.city.length >= 2 &&
    form.state.length === 2 &&
    form.zip_code.length >= 5 &&
    (landListing
      ? Boolean(form.mls_profile?.lot_size_acres || form.mls_profile?.lot_size_sqft)
      : (form.bedrooms || hasAnyBath || form.square_feet));

  // PriceEstimateCard is visual only, so mirror the outcomes a seller acts on
  // into a live region.
  const [priceEstimateAnnouncement, setPriceEstimateAnnouncement] = useState('');
  // Focus returns to the Price field after "Use Suggested Price" so the
  // accepted number is visible and immediately editable.
  const priceInputRef = useRef(null);
  // Tracks the button's disabled state exactly. Deliberately NOT gated on
  // `!priceEstimate`: editing a field after a successful estimate re-disables
  // the button, and that is precisely when the seller needs to be told why.
  const showEstimateRequirements = !canGetEstimate;

  // An estimate prices the details as they were when it ran. Once any input it
  // was built from changes (a bath added, the year built filled in) the range
  // on screen is for a different house, so say so and offer to update it.
  // Changing the price itself never stales it — price is not an input.
  const currentEstimateSignature = useMemo(
    () => fingerprintEstimateRequest(buildEstimateRequest(form)),
    [form],
  );
  const priceEstimateStale = Boolean(
    priceEstimate?.estimate &&
      priceEstimate.inputSignature &&
      priceEstimate.inputSignature !== currentEstimateSignature,
  );
  const missingEstimateDetails = ESTIMATE_KEY_DETAILS.filter((d) => !d.isSet(form)).map(
    (d) => d.label,
  );

  const handleGetPriceEstimate = async () => {
    setPriceEstimateLoading(true);
    setPriceEstimateError(null);
    setPriceEstimateRetryable(true);
    setPriceEstimate(null);
    setPriceEstimateAnnouncement('');
    try {
      const estimateRequest = buildEstimateRequest(form);
      // Fingerprint what is actually sent — the form may change mid-flight.
      const inputSignature = fingerprintEstimateRequest(estimateRequest);
      const result = await APIAgent.request('/api/agents/listing-price-estimate', 'POST', {
        ...estimateRequest,
        house_id: (isEditMode ? editHouse?.id : signingHouseId) || undefined,
      }, {}, { timeout: PRICE_ESTIMATE_TIMEOUT_MS });
      setPriceEstimate(result?.estimate ? { ...result, inputSignature } : result);
      if (result?.estimate) {
        setPriceEstimateAnnouncement(
          `Price estimate ready. Suggested ${formatPrice(result.estimate.recommended)}, ` +
          `range ${formatPrice(result.estimate.low)} to ${formatPrice(result.estimate.high)}.`,
        );
      }
    } catch (err) {
      console.error('Price estimate failed:', err);
      // 502 = the AI service is misconfigured for this environment; retrying
      // cannot help, so don't offer a "Try Again" button that will just fail
      // again. 503 (and everything else) is transient — keep the retry.
      setPriceEstimateRetryable(err.status !== 502);
      setPriceEstimateError(err.message || 'Failed to get price estimate');
    } finally {
      setPriceEstimateLoading(false);
    }
  };

  const handleUsePriceEstimate = (price) => {
    const rounded = Math.round(price);
    set('price', String(rounded));
    setPriceEstimateAnnouncement(`Listing price set to ${formatPrice(rounded)}.`);
    priceInputRef.current?.focus();
  };

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const setMls = (field, value) =>
    setForm((prev) => ({
      ...prev,
      mls_profile: {
        ...(prev.mls_profile || {}),
        [field]: value,
      },
    }));
  // Property type and its follow-up questions: questions that no longer
  // apply are cleared (design §9.2).
  const handlePaperworkAnswer = (field, value) => {
    setPaperworkError(null);
    setForm((prev) => ({
      ...prev,
      mls_profile: applyPaperworkAnswer(prev.mls_profile, field, value),
    }));
  };

  const STEPS = getSteps(form.state, billingEnabled);
  const isWI = form.state.toUpperCase() === 'WI';
  // The condition report out for DocuSign signature names this address, so
  // the server refuses an address change (409 condition_report_signing).
  const addressSigningLocked = isWI && wiDisclosureSigning;
  const addressFieldsDisabled = addressLocked || addressSigningLocked;
  const addressDescribedBy =
    [
      addressLocked && 'listing-address-locked-hint',
      addressSigningLocked && 'listing-address-signing-hint',
      addressSigningError && 'listing-address-signing-error',
    ]
      .filter(Boolean)
      .join(' ') || undefined;
  // -1 when billing is off (no Listing Fee step)
  const listingFeeStepIndex = STEPS.indexOf(LISTING_FEE_STEP);
  const ownershipStepIndex = STEPS.indexOf(OWNERSHIP_STEP);

  // Instant feedback from the frontend mirror; the server re-derives on save
  // and publish and is the authority (paperworkCategory.js).
  const paperworkResult = useMemo(() => deriveFromProfile(form.mls_profile), [form.mls_profile]);
  const savedPaperworkResult = useMemo(() => deriveFromProfile(savedProfile), [savedProfile]);
  const paperworkBlocked = isBlocked(paperworkResult);
  // Wisconsin routes paperwork: every question answered, nothing uncertain.
  const paperworkReady = !isWI || paperworkResult.status === STATUS_OK;
  const currentPaperworkKey = paperworkKey(paperworkResult);
  // What the server holds now; a listing saved before the questions counts
  // as residential / statutory.
  const savedPaperworkKey = paperworkKey(savedPaperworkResult) || LEGACY_PAPERWORK_KEY;
  const isVacantLand = paperworkResult.category === CATEGORY_VACANT_LAND;
  // Required Property Features still blank. Gates Continue on Property
  // Details and Publish, not Save as Draft.
  const missingFeatures = useMemo(
    () => missingMlsFields(form.mls_profile, { isVacantLand }),
    [form.mls_profile, isVacantLand],
  );
  const missingFeaturesMessage = (() => {
    if (missingFeatures.length === 0) return null;
    const names = missingFeatures.slice(0, 3).map((f) => f.label).join(', ');
    const more = missingFeatures.length > 3 ? ` and ${missingFeatures.length - 3} more` : '';
    return `Fill in every Property Features field (pick "None" where nothing applies). Still missing: ${names}${more}.`;
  })();
  // "{title}" in the disclosure copy: the report these answers call for, else
  // the one the server says the listing needs.
  const disclosureTitle =
    (isWI && disclosureTitleFor(form.state, paperworkResult)) ||
    wiDisclosureStatus?.title ||
    DISCLOSURE_TITLE_RECR;
  const paperworkNotAvailable =
    serverPaperwork?.available === false &&
    serverPaperwork?.category === paperworkResult.category &&
    paperworkResult.status === STATUS_OK;

  // Required uploads (the WI condo addendum): server state, then this session's.
  const serverUploads = useMemo(() => uploadsFromPaperwork(serverPaperwork), [serverPaperwork]);
  const requiredUploads = (isWI ? requiredUploadsFor(form.state, paperworkResult) : []).map((code) => ({
    code,
    ...(REQUIRED_UPLOAD_COPY[code] || { label: code, help: '', missing: '' }),
    ...(serverUploads[code] || { uploaded: false }),
    ...(sessionUploads[code] || {}),
  }));
  // Uploaded AND, where required, attested in the current version (gate G4).
  const missingUpload = requiredUploads.find((u) => !isUploadComplete(u)) || null;
  const handleRequiredUploaded = (code, info) => {
    setSessionUploads((prev) => ({ ...prev, [code]: info }));
    setPublishRefusal(null);
  };

  // Index of the WI disclosure step (-1 for non-WI)
  const wiStepIndex = isWI ? STEPS.indexOf(WI_DISCLOSURE_STEP) : -1;
  // Index of Property Details (first in every variant today, but derived so a
  // step inserted ahead of it cannot silently mis-gate the wizard)
  const propertyStepIndex = STEPS.indexOf(PROPERTY_STEP);
  // Index of Description & Photos (shifts with state, like the steps below it)
  const descriptionStepIndex = STEPS.indexOf(DESCRIPTION_STEP);
  // Index of the Sign Listing Agreement step (always present)
  const signStepIndex = STEPS.indexOf(SIGN_AGREEMENT_STEP);
  // Index of Review & Publish
  const reviewStepIndex = STEPS.indexOf(REVIEW_STEP);

  // Derived listing-agreement booleans. With DocuSign embedded signing the
  // agreement moves through none → sent/in_progress → signed (or voided), so we
  // surface the in-flight states in addition to the signed/stale ones.
  const agreementStatus = signedAgreement?.status || null;
  const hasSignedAgreement = Boolean(signedAgreement?.has_signed_agreement);
  // An envelope is out for signature but not yet completed (seller may have
  // abandoned mid-flow, or the prod webhook is still landing).
  const agreementPending =
    !hasSignedAgreement &&
    (agreementStatus === 'sent' || agreementStatus === 'in_progress');
  // A pending envelope minted from an older template must NOT be resumed: the
  // seller would re-enter a ceremony for a version they never consented to, and
  // `/initiate` would void the stale envelope and mint a fresh one against the
  // current template. Route those back through the consent gate instead.
  const agreementPendingStale =
    agreementPending &&
    signedAgreement?.template_version != null &&
    signedAgreement?.current_template_version != null &&
    signedAgreement.template_version !== signedAgreement.current_template_version;
  // Safe to hand the seller straight back into the existing ceremony.
  const canResumeSigning = agreementPending && !agreementPendingStale;
  // Last envelope was voided/declined — offer "Sign again".
  const agreementVoided =
    !hasSignedAgreement &&
    (agreementStatus === 'voided' || agreementStatus === 'declined');
  const signedVersionStale =
    hasSignedAgreement &&
    signedAgreement?.template_version != null &&
    signedAgreement?.current_template_version != null &&
    signedAgreement.template_version < signedAgreement.current_template_version;

  // Validation per step
  const isPropertyStepValid = () =>
    form.address.length >= 5 &&
    form.city.length >= 2 &&
    form.state.length === 2 &&
    form.zip_code.length >= 5;

  const isDescriptionStepValid = () => form.description.length >= 10;

  const isWiDisclosureStepValid = () => wiDisclosureCompleted;

  // Sign step advances when an unsigned-but-loaded agreement has been signed,
  // an already-signed (and current-version) agreement is on file, or the seller
  // hasn't reached the sign step yet (handled by signStepIndex check below).
  const isSignStepValid = () => hasSignedAgreement && !signedVersionStale;

  // Listing Fee: advance once nothing is owed (the server reports 0 when
  // billing is off or the fee is paid) — and only on a fee fetched during
  // THIS visit, after this visit's draft save. `listingFee` is cleared on
  // entry and on price changes; `draftReadyForStep` covers the one render
  // between arriving on the step and that reset.
  const isListingFeeStepValid = () =>
    draftReadyForStep && listingFee?.amount_due_cents === 0;

  // Ownership. The server decides whether the gate applies
  // (`verification_required`: grandfathered listings, kill switch). Until the
  // status has loaded it counts as required, so Publish waits for it.
  const ownershipRequired = ownershipStatus?.verification_required !== false;
  // Only picks the rejected-state copy (badge removed vs refund); it does NOT
  // decide whether ownership is required.
  const previouslyPublished = Boolean(editHouse?.first_published_at);
  const ownershipVerified = Boolean(
    ownershipStatus && (ownershipStatus.verified === true || isVerifiedStatus(ownershipStatus.status)),
  );
  // Continue is allowed while staff review is pending; Publish is not.
  const isOwnershipStepValid = () =>
    !ownershipRequired || ADVANCEABLE_STATUSES.includes(ownershipStatus?.status);

  const canAdvance = () => {
    // isPropertyStepValid() alone still gates Save as Draft: a draft may be
    // saved before the seller has settled on a price.
    if (step === propertyStepIndex) {
      return (
        isPropertyStepValid() &&
        hasListingPrice &&
        !paperworkBlocked &&
        paperworkReady &&
        missingFeatures.length === 0
      );
    }
    if (step === listingFeeStepIndex) return isListingFeeStepValid();
    if (step === ownershipStepIndex) return isOwnershipStepValid();
    if (step === descriptionStepIndex) return isDescriptionStepValid();
    if (isWI && step === wiStepIndex) return isWiDisclosureStepValid();
    if (step === signStepIndex) return isSignStepValid();
    return true;
  };

  // Bath room count (full + ¾ + half) — drives the publish gate's zero-bath
  // rule and the review summary's headline "N ba".
  const bathTotal = useMemo(
    () =>
      (form.full_baths || 0) +
      (form.three_quarter_baths || 0) +
      (form.half_baths || 0),
    [form.full_baths, form.three_quarter_baths, form.half_baths],
  );

  // MLS weighted decimal (full 1 · ¾ 0.75 · half 0.5). Shown next to the room
  // count, never alone — on its own "2.75" reads as "you have 2.75 bathrooms"
  // (QA sheet T-37), which is what BathBreakdownEditor now avoids too.
  const bathMlsDecimal = useMemo(
    () =>
      (form.full_baths || 0) +
      (form.three_quarter_baths || 0) * 0.75 +
      (form.half_baths || 0) * 0.5,
    [form.full_baths, form.three_quarter_baths, form.half_baths],
  );

  // Rooms publish errors are surfaced from RoomsEditor's exposed helper.
  const roomsErrors = useMemo(
    () => getRoomsPublishErrors(form.rooms || []),
    [form.rooms],
  );

  // Three-way publish gate per design spec §5, extended with MLS-grade
  // listing data gates: zero-bath confirmation + rooms validity.
  // Land has no bathrooms or rooms to describe (design §9.3).
  const needsHomeRules = !isVacantLand;

  // The seller-facing reason the paperwork isn't settled yet, or null.
  const paperworkBlocker = () => {
    if (paperworkBlocked) {
      return resultMessage(form.state, paperworkResult) || GENERIC_BLOCK_MESSAGE;
    }
    if (!paperworkReady) return copyText('WI.paperwork.publish.category_required');
    return null;
  };

  /**
   * Why ownership verification still blocks publishing, or null when it
   * doesn't (verified, or not required for this listing).
   *
   * @returns {string|null}
   */
  const ownershipBlocker = () => {
    if (!ownershipRequired || ownershipVerified) return null;
    if (!ownershipStatus) {
      return ownershipError
        ? "We couldn't load the status of your ownership documents. Reload to try again."
        : 'Checking the status of your ownership documents…';
    }
    return ownershipStatus?.status === 'needs_review'
      ? 'Your ownership documents are under review. You can publish once the review is complete.'
      : 'Confirm your ownership or authority to sell this property before publishing.';
  };

  const canPublish = () => {
    // Drafts saved before a price was required can still reach this step.
    if (!hasListingPrice) return false;
    if (paperworkBlocked || !paperworkReady) return false;
    // Drafts saved before every feature was required can still reach this step.
    if (missingFeatures.length > 0) return false;
    if (ownershipRequired && !ownershipVerified) return false;
    if (isWI && !wiDisclosureCompleted) return false;
    if (!hasSignedAgreement) return false;
    if (signedVersionStale) return false;
    if (missingUpload) return false;
    if (needsHomeRules && bathTotal === 0 && !form.bath_zero_confirmed) return false;
    if (needsHomeRules && roomsErrors.length > 0) return false;
    return true;
  };

  const publishDisabledTooltip = () => {
    if (!hasListingPrice) {
      return 'Set a listing price on Property Details before publishing.';
    }
    const paperwork = paperworkBlocker();
    if (paperwork) return paperwork;
    if (missingFeaturesMessage) {
      return `${missingFeaturesMessage} They're under Property Features on Property Details.`;
    }
    const ownership = ownershipBlocker();
    if (ownership) return ownership;
    if (isWI && !wiDisclosureCompleted) {
      // Sent to DocuSign but not signed yet: say what's left.
      if (wiDisclosureSigning) return disclosureSigningText(disclosureTitle);
      // WI.disclosure.publish_tooltip (legal copy B2).
      return copyText('WI.disclosure.publish_tooltip', { title: disclosureTitle });
    }
    if (!hasSignedAgreement) {
      return 'Sign the listing agreement before publishing.';
    }
    if (signedVersionStale) {
      return 'The listing agreement template was updated. Re-sign to publish.';
    }
    if (missingUpload) {
      return missingUpload.missing;
    }
    if (needsHomeRules && bathTotal === 0 && !form.bath_zero_confirmed) {
      return 'Add at least one bathroom, or confirm this property has none.';
    }
    if (needsHomeRules && roomsErrors.length > 0) {
      return roomsErrors[0];
    }
    return undefined;
  };

  // DocuSign embedded-signing gate (legal MUST): the seller must read the
  // agreement, see the six disclaimers, and acknowledge ESIGN consent on a
  // screen we control before we hand them off to DocuSign. Typed-name signing
  // is gone — the cryptographic signature is captured inside DocuSign.
  // `rendered_html`, not just `agreementPreview`: the read gate below only
  // observes once the agreement body is on screen, so if a preview ever comes
  // back without it the scroll gate could never be satisfied and this button
  // would sit permanently disabled behind a tooltip telling the seller to
  // scroll through an agreement that isn't rendered. Both gates key off the
  // same fact, so the failure surfaces as "preview unavailable" instead.
  const canSign =
    !signSubmitting &&
    Boolean(agreementPreview?.rendered_html) &&
    consentChecked &&
    previewScrolledToBottom;

  const signDisabledTooltip = canSign
    ? undefined
    : 'Check the consent box and scroll through the agreement to continue to DocuSign.';

  const continueFromSignTooltip = hasSignedAgreement
    ? signedVersionStale
      ? 'A newer agreement version is available. Re-sign to continue.'
      : undefined
    : 'Sign the listing agreement to continue.';

  // Every gated step needs to say WHY Continue is inert. Without this the
  // seller clicks a disabled button and nothing happens, with no error, no
  // tooltip and no hint — which is exactly how the WI disclosure step used to
  // behave. Returns undefined when the step can advance, so an enabled button
  // carries no tooltip.
  const continueTooltip = (() => {
    if (canAdvance()) return undefined;
    if (step === propertyStepIndex) {
      if (!isPropertyStepValid()) {
        return 'Fill in the street address, city, state and ZIP code to continue.';
      }
      if (paperworkBlocked || !paperworkReady) {
        return (
          resultMessage(form.state, paperworkResult) ||
          paperworkBlocker()
        );
      }
      if (missingFeaturesMessage) return missingFeaturesMessage;
      return 'Set a listing price to continue. The AI estimate can suggest one.';
    }
    if (step === listingFeeStepIndex) {
      return 'Pay the Full Service fee to continue.';
    }
    if (step === ownershipStepIndex) {
      if (ownershipStatus?.status === 'rejected') {
        return "We couldn't confirm your ownership or authority from the documents provided, so this listing can't continue.";
      }
      if (ownershipStatus?.status === 'processing') {
        return 'Your document is still being checked. Select "Check again" in a moment.';
      }
      return 'Upload your ownership document and submit it to continue.';
    }
    if (step === descriptionStepIndex) {
      return 'Add a property description of at least 10 characters to continue.';
    }
    if (isWI && step === wiStepIndex) {
      return 'Answer every required question, preview and sign the report, then select "Complete report" to continue.';
    }
    if (step === signStepIndex) return continueFromSignTooltip;
    return undefined;
  })();

  // Why the step's main button is disabled, shown under the buttons.
  const navBlockedReason = submitting
    ? undefined
    : step < reviewStepIndex
      ? (!canAdvance() ? continueTooltip : undefined)
      : (!canPublish() ? publishDisabledTooltip() : undefined);

  const renderMlsField = (field) => {
    const value = form.mls_profile?.[field.key] ?? (field.type === 'multiselect' ? [] : '');
    const required = isMlsFieldRequired(field, form.mls_profile, { isVacantLand });
    const label = `${field.label || toLabel(field.key)}${required ? ' *' : ''}`;
    const controlId = `mls-${field.key}`;

    if (field.type === 'textarea') {
      return (
        <div key={field.key} className="listing-form__field listing-form__field--full">
          <label className="listing-form__label" htmlFor={controlId}>{label}</label>
          <textarea
            id={controlId}
            aria-required={required || undefined}
            className="listing-form__textarea"
            value={value}
            onChange={(e) => setMls(field.key, e.target.value)}
            placeholder={field.placeholder || ''}
            rows={3}
          />
        </div>
      );
    }

    if (field.type === 'multiselect') {
      const selected = Array.isArray(value) ? value : [];
      const full = Boolean(field.maxSelections) && selected.length >= field.maxSelections;
      const labelId = `mls-label-${field.key}`;
      return (
        <div key={field.key} className="listing-form__field listing-form__field--full">
          <label className="listing-form__label" id={labelId}>
            {label}
            {field.maxSelections && (
              <span className="listing-form__label-note"> (up to {field.maxSelections})</span>
            )}
          </label>
          <div className="listing-form__chips" role="group" aria-labelledby={labelId}>
            {(field.options || []).map((opt) => (
              <button
                key={opt}
                type="button"
                className={`listing-form__chip ${selected.includes(opt) ? 'listing-form__chip--active' : ''}`}
                aria-pressed={selected.includes(opt)}
                disabled={full && !selected.includes(opt)}
                onClick={() => setMls(field.key, toggleMultiselectOption(field, selected, opt))}
              >
                {selected.includes(opt) && <span className="listing-form__chip-check">✓</span>}
                {opt}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.key} className="listing-form__field">
          <label className="listing-form__label" htmlFor={controlId}>{label}</label>
          <select
            id={controlId}
            aria-required={required || undefined}
            className="listing-form__input"
            value={value}
            onChange={(e) => setMls(field.key, e.target.value)}
          >
            {(field.options || ['']).map((opt) => (
              <option key={opt || 'empty'} value={opt}>
                {opt || 'Select...'}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div key={field.key} className="listing-form__field">
        <label className="listing-form__label" htmlFor={controlId}>{label}</label>
        <input
          id={controlId}
          aria-required={required || undefined}
          className="listing-form__input"
          type={field.type === 'number' ? 'number' : 'text'}
          value={value}
          onChange={(e) => setMls(field.key, e.target.value)}
          placeholder={field.placeholder || ''}
          min={field.type === 'number' ? '0' : undefined}
          step={field.step || undefined}
        />
      </div>
    );
  };

  const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  // Compress at most this many photos at once. Compression is largely
  // main-thread work, so throttling keeps the UI responsive when a seller
  // adds many photos (e.g. 20) at once.
  const COMPRESS_CONCURRENCY = 3;

  const addImageFiles = (files) => {
    const valid = files.filter((f) => ACCEPTED_TYPES.includes(f.type));
    if (valid.length === 0) return;
    const items = valid.map((file) => ({
      id: photoIdRef.current++,
      file,
      preview: URL.createObjectURL(file),
      processing: true,
    }));
    setNewPhotos((prev) => [...prev, ...items]);
    compressNewPhotos(items);
  };

  // Compress the given items in the background with bounded concurrency,
  // swapping each item's file for the compressed version as it completes.
  const compressNewPhotos = (items) => {
    let cursor = 0;
    const worker = async () => {
      while (cursor < items.length) {
        const item = items[cursor++];
        const compressed = await compressImage(item.file);
        setNewPhotos((prev) =>
          prev.map((p) =>
            p.id === item.id ? { ...p, file: compressed, processing: false } : p,
          ),
        );
      }
    };
    const lanes = Math.min(COMPRESS_CONCURRENCY, items.length);
    for (let i = 0; i < lanes; i++) worker();
  };

  const handleImageAdd = (e) => {
    const files = Array.from(e.target.files);
    addImageFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    addImageFiles(files);
  };

  const handleNewPhotoRemove = (id) => {
    setNewPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleExistingImageRemove = (idx) => {
    setExistingImageUrls((prev) => prev.filter((_, i) => i !== idx));
  };

  // Build a HouseCreate / HouseUpdate payload from the current form state.
  // Extracted so both `handleSubmit` and the sign-step auto-draft can share it.
  const buildListingPayload = (publishNow) => {
    const normalizedMlsProfile = Object.fromEntries(
      Object.entries(form.mls_profile || {}).filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        return value !== null && value !== undefined && `${value}`.trim() !== '';
      }),
    );

    if (priceEstimate?.estimate) {
      normalizedMlsProfile.ai_price_estimate = {
        id: priceEstimate.id || null,
        low: priceEstimate.estimate.low,
        recommended: priceEstimate.estimate.recommended,
        high: priceEstimate.estimate.high,
        confidence: priceEstimate.confidence || null,
        generated_at: priceEstimate.generated_at || null,
        disclaimer: priceEstimate.disclaimer || null,
        factors: priceEstimate.factors || [],
        // Lets a later edit session tell whether the saved range still
        // matches the saved details.
        input_signature: priceEstimate.inputSignature || null,
      };
      normalizedMlsProfile.ai_comparable_properties = priceEstimate.comparables || [];
    }

    // Strip in-progress rows (missing L/W) when publishing, but keep them on
    // draft so the seller can resume. Server validates the publish-time set;
    // partial rows would otherwise 422 the whole save.
    const cleanedRooms = (form.rooms || [])
      .filter((r) => {
        if (!publishNow) return true;
        const l = parseFloat(r.length_ft);
        const w = parseFloat(r.width_ft);
        return !Number.isNaN(l) && !Number.isNaN(w) && r.room_type && r.level;
      })
      .map((r) => {
        const l = parseFloat(r.length_ft);
        const w = parseFloat(r.width_ft);
        return {
          // Drop the synthetic `id` — the API generates it; reorders are
          // expressed by array order on POST/PUT per architect §6.
          room_type: r.room_type || undefined,
          level: r.level || undefined,
          length_ft: Number.isNaN(l) ? undefined : l,
          width_ft: Number.isNaN(w) ? undefined : w,
          flooring: r.flooring || undefined,
          features: r.features ? r.features.trim() : undefined,
        };
      });

    return {
      address: form.address.trim(),
      city: form.city.trim(),
      state: form.state.toUpperCase().trim(),
      zip_code: form.zip_code.trim(),
      price: form.price ? parseFloat(form.price) : undefined,
      bedrooms: form.bedrooms ? parseInt(form.bedrooms, 10) : undefined,
      // `bathrooms` is now derived server-side from the breakdown — we
      // intentionally omit it from POST/PUT payloads (per architect §6
      // breaking change). Server returns it on GET.
      square_feet: form.square_feet ? parseFloat(form.square_feet) : undefined,
      description: form.description.trim(),
      mls_profile: normalizedMlsProfile,
      full_baths: form.full_baths || 0,
      three_quarter_baths: form.three_quarter_baths || 0,
      half_baths: form.half_baths || 0,
      rooms: cleanedRooms,
      // No listing_status: an update never sends one (status changes go
      // through PUT /houses/{id}/status only, and the copy loaded with the
      // form can be stale — an offer accepted meanwhile moves it to pending,
      // which the server refuses as a relist, QA S13). A create adds 'draft'
      // in persistListing.
    };
  };

  // Persist the listing (create or update), and on publish take it live.
  // Returns the house object.
  //
  // Publish routing (Full Service charge, PR5): the save itself never carries
  // `listing_status: 'active'`. The listing is saved in its current non-live
  // state, then flipped via `PUT /houses/{id}/status` — the only route that
  // enforces the publish gates (signed-agreement 409 and, when billing is
  // enabled, the Full Service payment 402). POST /houses and PUT /houses/{id}
  // enforce neither, so sending 'active' through them skipped both gates and the
  // 402 → charge-modal path below could never fire. When billing is disabled
  // the status route has no payment gate, so publishing looks the same.
  const persistListing = async (publishNow) => {
    const payload = buildListingPayload(publishNow);

    // Update rather than create whenever a house already exists: the one being
    // edited, or the draft the WI / sign step saved silently in create mode.
    // Creating again from there left a duplicate listing behind.
    const existingHouseId = isEditMode ? editHouse.id : signingHouseId;

    // A change of category or disclosure basis after the report was signed
    // voids that report on the server (legal §4), so ask first.
    const nextResult = deriveFromProfile(payload.mls_profile);
    const nextKey = paperworkKey(nextResult);
    const paperworkChanged = Boolean(existingHouseId && nextKey && nextKey !== savedPaperworkKey);
    const willVoid = paperworkChanged && isWI && wiDisclosureCompleted;
    if (willVoid) {
      const confirmText = copyText('WI.paperwork.change.confirm', {
        old_title: wiDisclosureStatus?.title || disclosureTitleFor(form.state, savedPaperworkResult) || DISCLOSURE_TITLE_RECR,
        new_title: disclosureTitleFor(form.state, nextResult) || DISCLOSURE_TITLE_RECR,
      });
      if (!window.confirm(confirmText)) throw saveCancelled();
    }

    let house;
    if (existingHouseId) {
      payload.image_urls = existingImageUrls;
      house = await APIAgent.updateHouse(existingHouseId, payload);
    } else {
      payload.seller_id = sellerId;
      // Never 'active', even when publishing: going live is the status call below.
      payload.listing_status = 'draft';
      house = await APIAgent.createHouse(payload);
    }
    const houseId = house?.id || existingHouseId;

    // The server now derives its category from this profile.
    setSavedProfile(payload.mls_profile);
    if (house && Object.prototype.hasOwnProperty.call(house, 'paperwork')) {
      setServerPaperwork(house.paperwork || null);
    }
    setPaperworkError(null);
    setAddressSigningError(null);
    if (paperworkChanged) {
      if (willVoid) voidPendingRef.current = true;
      setDisclosureStatusNonce((n) => n + 1);
    }

    // Upload new images if any. Photos are compressed in the background as
    // they're added; compress any still in flight here so the upload always
    // sends the smaller versions regardless of timing.
    const pendingPhotos = newPhotos.filter((p) => !uploadedPhotoIdsRef.current.has(p.id));
    if (pendingPhotos.length > 0 && houseId) {
      const uploading = pendingPhotos;
      const files = await Promise.all(
        uploading.map((p) => (p.processing ? compressImage(p.file) : p.file)),
      );
      const uploaded = await APIAgent.uploadHouseImages(houseId, files);
      uploading.forEach((p) => uploadedPhotoIdsRef.current.add(p.id));
      // Those photos now belong to the house: swap the local previews for the
      // server's cumulative list so a later save in this session doesn't
      // upload them again. Without that list, keep the previews rather than
      // make the seller's photos vanish from the form.
      const serverUrls = Array.isArray(uploaded?.image_urls) ? uploaded.image_urls : [];
      if (serverUrls.length > 0) {
        const uploadedIds = new Set(uploading.map((p) => p.id));
        setNewPhotos((prev) => prev.filter((p) => !uploadedIds.has(p.id)));
        setExistingImageUrls(serverUrls);
      }
    }

    if (publishNow && houseId) {
      // Remember which house the publish/charge applies to (used by the charge
      // modal + the post-charge publish finalize).
      publishHouseIdRef.current = houseId;
      house = await APIAgent.updateListingStatus(houseId, 'active');
    }

    return house;
  };

  /**
   * Rooms-editor autosave callback. Per architect §11 open-question 1, the
   * wizard re-saves the whole draft rather than introducing a rooms-only
   * endpoint — that's the resolved decision in the slice brief.
   *
   * Throws on failure so the RoomsEditor's autosave chip flips to warning.
   */
  const handleRoomsAutosave = useCallback(
    async (nextRooms) => {
      // Quietly skip autosave in pure create mode — there's nothing to PATCH
      // until we have a house id from a "Save as Draft" or the sign step's
      // silent draft. Mark the chip "Saved" so the seller doesn't see a
      // spinner that never resolves.
      if (!isEditMode && !signingHouseId) {
        return;
      }
      try {
        // Build a payload that uses the *next* rooms array (not the React
        // state, which hasn't applied yet).
        const cleaned = (nextRooms || []).map((r) => {
          const l = parseFloat(r.length_ft);
          const w = parseFloat(r.width_ft);
          return {
            room_type: r.room_type || undefined,
            level: r.level || undefined,
            length_ft: Number.isNaN(l) ? undefined : l,
            width_ft: Number.isNaN(w) ? undefined : w,
            flooring: r.flooring || undefined,
            features: r.features ? r.features.trim() : undefined,
          };
        });

        const payload = buildListingPayload(false);
        payload.rooms = cleaned;
        const houseId = signingHouseId || editHouse?.id;
        if (!houseId) return;
        // A background save never changes the paperwork in a way the seller
        // must see first: a hard block isn't saveable, and a category change
        // after the report was signed voids the report. Those wait for an
        // explicit save.
        const nextKey = paperworkKey(deriveFromProfile(payload.mls_profile));
        if (
          isBlocked(deriveFromProfile(payload.mls_profile)) ||
          (isWI && wiDisclosureCompleted && nextKey && nextKey !== savedPaperworkKey)
        ) {
          payload.mls_profile = withSavedPaperworkFacts(payload.mls_profile, savedProfile);
        }
        payload.image_urls = existingImageUrls;
        await APIAgent.updateHouse(houseId, payload);
        setSavedProfile(payload.mls_profile);
        const savedKeyNow = paperworkKey(deriveFromProfile(payload.mls_profile)) || LEGACY_PAPERWORK_KEY;
        if (savedKeyNow !== savedPaperworkKey) {
          setDisclosureStatusNonce((n) => n + 1);
        }
      } catch (err) {
        // Rethrow so RoomsEditor flips the chip — never silently swallow.
        throw err;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isEditMode,
      signingHouseId,
      editHouse,
      form,
      existingImageUrls,
      savedProfile,
      savedPaperworkKey,
      wiDisclosureCompleted,
    ],
  );

  /**
   * Ensure we have a `house_id` to bind a signed listing-agreement to.
   * In create mode the wizard hasn't saved yet when the seller reaches the
   * sign step, so we silently save the listing as a draft and use the
   * returned id.
   */
  const ensureSigningHouseId = useCallback(async () => {
    if (signingHouseId) return signingHouseId;
    if (isEditMode && editHouse?.id) {
      setSigningHouseId(editHouse.id);
      return editHouse.id;
    }
    const house = await persistListing(false);
    const id = house?.id ?? null;
    if (id) {
      setSigningHouseId(id);
      // Surface a non-error notice via onListingSaved so the dashboard knows.
      if (onListingSaved) onListingSaved(house);
    }
    return id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signingHouseId, isEditMode, editHouse, form, priceEstimate, newPhotos, existingImageUrls]);


  /**
   * Save the CURRENT form on entering a step that attaches to the draft
   * (Listing Fee, Confirm Ownership, WI Seller Disclosure, Sign Listing
   * Agreement) and return the house id.
   *
   * Create mode: the first such step creates the draft; every later one
   * updates it (description, price, address…) and uploads photos not yet
   * uploaded. Without this, a draft created at Confirm Ownership reached the
   * DocuSign redirect without the description or photos added after it.
   * Edit mode keeps its old behavior (no silent save of a live listing).
   */
  const saveDraftForStep = async () => {
    const forStep = step;
    try {
      if (isEditMode || !signingHouseId) return await ensureSigningHouseId();
      const house = await persistListing(false);
      if (house && onListingSaved) onListingSaved(house);
      // An address change revokes a verification server-side; re-read it.
      loadOwnershipStatus(signingHouseId);
      // The draft we just updated — its id doesn't change.
      return signingHouseId;
    } finally {
      // Even after a failed update, let the step show (the error is surfaced
      // above it) rather than leave the seller on "Saving…".
      setStepSaveDone(forStep);
    }
  };

  /**
   * 409 `condition_report_signing`: the address changed while the condition
   * report is out for DocuSign signature. The server's message goes beside
   * the address fields on Property Details; nothing in the form is reset.
   */
  const showAddressSigningRefusal = (err) => {
    setAddressSigningError(refusalMessage(err) || ADDRESS_SIGNING_LOCKED_MESSAGE);
    setStep(propertyStepIndex);
    // The report may have gone out for signature since this page loaded.
    setDisclosureStatusNonce((n) => n + 1);
  };

  /**
   * A step-entry save can end two paperwork ways (design §9.4): the seller
   * declined a change that would void their signed report, or the server
   * refused the paperwork (blocked, or locked). Either way they go back to
   * the property-type questions on Property Details, with the server's
   * message when there is one. Shared by every step that saves on entry
   * (Listing Fee, Confirm Ownership, Seller Disclosure, Sign Listing Agreement).
   *
   * @param {Error & {cancelled?: boolean, detail?: any}} err - What the save threw.
   * @returns {boolean} True when the error was one of those two and is handled.
   */
  const handleStepSavePaperworkOutcome = (err) => {
    if (err?.cancelled) {
      // The seller kept their signed report: back to the type question.
      setStep(propertyStepIndex);
      return true;
    }
    if (isConditionReportSigning(err)) {
      showAddressSigningRefusal(err);
      return true;
    }
    const code = paperworkErrorCode(err);
    if (!code) return false;
    setPaperworkError(
      code === PAPERWORK_CATEGORY_LOCKED
        ? paperworkLockMessage(err, form.state)
        : refusalMessage(err) || err.message,
    );
    setStep(propertyStepIndex);
    return true;
  };

  // Fetch existing signed-agreement status whenever we know the house id.
  // Runs in edit mode immediately, and after the sign-step auto-draft saves.
  useEffect(() => {
    if (!signingHouseId) return;
    if (typeof APIAgent.getListingAgreementStatus !== 'function') return;
    let cancelled = false;
    (async () => {
      try {
        const status = await APIAgent.getListingAgreementStatus(signingHouseId);
        if (!cancelled) setSignedAgreement(status);
      } catch (err) {
        if (!cancelled) {
          // A 404 here means the endpoint returned no record yet — that's OK
          // and is treated as "not signed".
          console.warn('Listing agreement status lookup failed:', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [signingHouseId]);

  // The WI condition report attaches to a house, so a draft listing has to
  // exist before the seller can start answering. Mirrors the sign step.
  // The server picks the disclosure form from the SAVED answers, so a changed
  // property type is saved before the step loads its form (design §9.4).
  const disclosureNeedsResave = Boolean(
    signingHouseId && currentPaperworkKey && currentPaperworkKey !== savedPaperworkKey,
  );
  useEffect(() => {
    if (!isWI || step !== wiStepIndex) return;
    let cancelled = false;
    (async () => {
      try {
        // Create mode: creates the draft, or saves the current form to it
        // (a changed property type included). Marks the step's save done.
        const id = await saveDraftForStep();
        if (cancelled || !id) return;
        // Edit mode: saveDraftForStep never saves an existing listing silently,
        // but a changed property type must reach the server before the step
        // loads its form.
        if (isEditMode && disclosureNeedsResave) {
          await persistListing(false);
        }
      } catch (err) {
        if (cancelled) return;
        if (handleStepSavePaperworkOutcome(err)) return;
        console.error('Could not prepare listing for the condition report:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWI, step, wiStepIndex]);

  // Listing Fee and Confirm Ownership both attach to a house, so entering
  // either saves the current form first (same as the WI and sign steps): the
  // fee tier follows the saved price and ownership matching the saved address.
  useEffect(() => {
    if (step !== listingFeeStepIndex && step !== ownershipStepIndex) return;
    let cancelled = false;
    (async () => {
      try {
        const id = await saveDraftForStep();
        if (cancelled || !id) return;
      } catch (err) {
        if (cancelled) return;
        if (handleStepSavePaperworkOutcome(err)) return;
        console.error('Could not save the listing draft:', err);
        setError(err.message || 'Could not save your listing draft.');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, listingFeeStepIndex, ownershipStepIndex]);

  // `quiet` re-reads without the loading state, for background refreshes.
  const loadOwnershipStatus = useCallback(async (houseId, { quiet = false } = {}) => {
    if (!houseId) return;
    if (!quiet) {
      setOwnershipLoading(true);
      setOwnershipError(null);
    }
    try {
      const status = await ownershipVerificationAgent.getStatus(houseId);
      setOwnershipStatus(status || null);
      if (quiet) setOwnershipError(null);
    } catch (err) {
      console.error('Ownership verification status lookup failed:', err?.status);
      if (!quiet) {
        setOwnershipError(err?.message || "We couldn't load the status of your ownership documents.");
      }
    } finally {
      if (!quiet) setOwnershipLoading(false);
    }
  }, []);

  // Load ownership status whenever the house id is known (edit mode on mount,
  // create mode once the draft saves) so the publish gate is right everywhere.
  useEffect(() => {
    if (!signingHouseId) return;
    loadOwnershipStatus(signingHouseId);
  }, [signingHouseId, loadOwnershipStatus]);

  // QA F5: a staff decision lands while the wizard is open (the seller is told
  // they can keep working). Re-read on entering Confirm Ownership or Review &
  // Publish, in edit mode too, and keep checking there while it's under review,
  // so an approval unlocks Publish without a page reload.
  const onOwnershipOrReviewStep = step === ownershipStepIndex || step === reviewStepIndex;
  const ownershipUnderReview = ownershipStatus?.status === 'needs_review';
  useEffect(() => {
    if (!signingHouseId || !onOwnershipOrReviewStep) return;
    loadOwnershipStatus(signingHouseId, { quiet: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  useEffect(() => {
    if (!signingHouseId || !onOwnershipOrReviewStep || !ownershipUnderReview) return undefined;
    const timer = setInterval(
      () => loadOwnershipStatus(signingHouseId, { quiet: true }),
      OWNERSHIP_REVIEW_POLL_MS,
    );
    return () => clearInterval(timer);
  }, [signingHouseId, onOwnershipOrReviewStep, ownershipUnderReview, loadOwnershipStatus]);

  const handleListingFeeChange = useCallback((fee) => setListingFee(fee), []);

  // Load disclosure status whenever the house id is known, so the publish gate
  // and step validity are correct on re-entry and in edit mode. Re-read after
  // a paperwork change: the listing may now need a different report, and a
  // report signed for the old paperwork has been voided.
  useEffect(() => {
    if (!isWI || !signingHouseId) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await DisclosureAPI.getStatus(signingHouseId);
        if (cancelled) return;
        setWiDisclosureStatus(status);
        // Kept apart: the report form's own payload replaces the status above.
        setDisclosureSigningError(
          typeof status?.last_signing_error === 'string' ? status.last_signing_error : null,
        );
        if (voidPendingRef.current) {
          voidPendingRef.current = false;
          if (!isDisclosureComplete(status)) setDisclosureVoided(true);
        }
      } catch (err) {
        // No disclosure yet is the expected empty state, not an error.
        if (!cancelled) setWiDisclosureStatus(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isWI, signingHouseId, disclosureStatusNonce]);

  // When the seller arrives on the sign step, save the current form (the
  // DocuSign redirect is a full page load, so anything unsaved is lost) and
  // load the agreement preview.
  useEffect(() => {
    if (step !== signStepIndex) return;
    if (typeof APIAgent.previewListingAgreement !== 'function') return;
    let cancelled = false;
    (async () => {
      try {
        setAgreementError(null);
        const houseId = await saveDraftForStep();
        if (cancelled || !houseId) return;
        // If we already have a current-version signed record on file, skip
        // loading the preview — we'll render the post-sign card directly.
        if (
          signedAgreement?.has_signed_agreement &&
          signedAgreement?.template_version === signedAgreement?.current_template_version
        ) {
          return;
        }
        setAgreementLoading(true);
        const preview = await APIAgent.previewListingAgreement(houseId);
        if (!cancelled) setAgreementPreview(preview);
      } catch (err) {
        if (!cancelled && !handleStepSavePaperworkOutcome(err)) {
          console.error('Listing agreement preview failed:', err);
          setAgreementError(err.message || 'Could not load the agreement.');
        }
      } finally {
        if (!cancelled) setAgreementLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, signStepIndex]);

  // Scroll-to-bottom sentinel: observed once-per-mount.
  //
  // The sentinel renders inside the preview body unconditionally, including
  // while the agreement is still loading. In that state the body is a single
  // "Loading agreement…" line, so the sentinel sits within the visible box and
  // the observer's FIRST (automatic) observation reports it intersecting —
  // latching the gate open before the seller has seen a word of the agreement.
  // Observed on deployed dev: consent alone enabled the sign button at
  // scrollTop 0 of 8496px.
  //
  // So: don't observe until the agreement has actually rendered, and treat an
  // intersection as proof only if the container really is scrolled to the
  // bottom (or is too short to scroll, which is a legitimate read-it-all).
  useEffect(() => {
    if (step !== signStepIndex) return;
    if (hasSignedAgreement && !signedVersionStale) return;
    if (!agreementPreview?.rendered_html) return;
    const sentinel = previewBottomSentinelRef.current;
    const root = previewScrollContainerRef.current;
    if (!sentinel || !root) return;
    if (typeof IntersectionObserver !== 'function') {
      setPreviewScrolledToBottom(true);
      return;
    }
    // Sub-pixel scroll positions and zoom make an exact equality test flaky.
    const SCROLL_EPSILON_PX = 2;
    const isAtBottom = () =>
      root.scrollTop + root.clientHeight >= root.scrollHeight - SCROLL_EPSILON_PX;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && isAtBottom()) setPreviewScrolledToBottom(true);
        });
      },
      { root, threshold: 1.0 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [step, signStepIndex, agreementPreview, hasSignedAgreement, signedVersionStale]);

  // After a successful sign, focus the confirmation card for a11y.
  useEffect(() => {
    if (hasSignedAgreement && signedConfirmRef.current && step === signStepIndex) {
      signedConfirmRef.current.focus();
    }
  }, [hasSignedAgreement, step, signStepIndex]);

  const handleRetryLoadAgreement = async () => {
    if (!signingHouseId) return;
    if (typeof APIAgent.previewListingAgreement !== 'function') return;
    setAgreementError(null);
    setAgreementLoading(true);
    try {
      const preview = await APIAgent.previewListingAgreement(signingHouseId);
      setAgreementPreview(preview);
    } catch (err) {
      console.error('Listing agreement preview retry failed:', err);
      setAgreementError(err.message || 'Could not load the agreement.');
    } finally {
      setAgreementLoading(false);
    }
  };

  // Re-check the agreement status via /reconcile (reads the live DocuSign
  // envelope state). Used by the "Your signature is being recorded…" pending
  // banner so a seller who returned mid-flow can pull the latest state.
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const handleRefreshAgreementStatus = async () => {
    if (!signingHouseId) return;
    if (typeof APIAgent.reconcileListingAgreement !== 'function') return;
    setRefreshingStatus(true);
    try {
      const status = await APIAgent.reconcileListingAgreement(
        signingHouseId,
        signedAgreement?.document_id,
      );
      setSignedAgreement(status);
    } catch (err) {
      console.warn('Listing agreement reconcile refresh failed:', err);
    } finally {
      setRefreshingStatus(false);
    }
  };

  const handleResignLatest = () => {
    // Reset the pre-sign consent gate so the seller re-acknowledges the new
    // template version before being handed off to DocuSign again. The backend
    // `/initiate` voids any stale envelope and mints a fresh one.
    setSignedAgreement((prev) =>
      prev ? { ...prev, has_signed_agreement: false } : prev,
    );
    setConsentChecked(false);
    setPreviewScrolledToBottom(false);
    setAgreementPreview(null);
    setShowSignedAgreement(false);
    handleRetryLoadAgreement();
  };

  /**
   * Hand the seller off to DocuSign embedded signing.
   *
   * Replaces the old local typed-name submit. After the seller has read the
   * agreement, seen the disclaimers, and acknowledged ESIGN consent on this
   * screen, we call `/initiate` (which creates/reuses the envelope) and
   * redirect the browser to the returned DocuSign embedded recipient-view URL.
   * DocuSign then redirects back to the agreement-return route, which calls
   * `/reconcile` to finalize and surface the signed/pending/voided result.
   */
  const handleSignAgreement = async () => {
    if (!canSign || !agreementPreview || !signingHouseId) return;
    if (typeof APIAgent.initiateListingAgreement !== 'function') return;
    setSignSubmitting(true);
    setSignError(null);
    try {
      const result = await APIAgent.initiateListingAgreement(signingHouseId, {
        consent_version: agreementPreview.consent_version,
        doc_sha256: agreementPreview.template_sha256,
      });
      const signingUrl = result?.signing_url;
      if (!signingUrl) {
        throw new Error('DocuSign did not return a signing link. Please try again.');
      }
      setSignedAnnouncement('Redirecting you to DocuSign to sign your listing agreement…');
      // Full-page redirect into the DocuSign embedded recipient view. On
      // completion DocuSign redirects back to the agreement-return route.
      window.location.assign(signingUrl);
    } catch (err) {
      console.error('Initiate listing agreement signing failed:', err);
      setSignError(
        err.message ||
          "We couldn't start the signing session. Please try again.",
      );
      setSignSubmitting(false);
    }
    // Note: on success we intentionally leave signSubmitting true — the page is
    // navigating away to DocuSign, so we keep the button in its busy state.
  };

  /**
   * Re-enter an in-flight DocuSign ceremony the seller never finished.
   *
   * Deliberately skips the local consent gate: the seller already acknowledged
   * ESIGN consent when this envelope was minted (the audit row records the
   * consent_version), and `/initiate` reuses the in-flight envelope rather than
   * minting a second one — so this resumes the SAME agreement rather than
   * starting a new one. DocuSign also re-presents its own electronic-records
   * consent at the start of every signing session. Guarded by
   * `canResumeSigning`, which refuses to resume once the active template has
   * moved on (that case re-renders the full pre-sign gate instead).
   */
  const handleResumeSigning = async () => {
    if (!canResumeSigning || signSubmitting) return;
    if (!agreementPreview || !signingHouseId) return;
    if (typeof APIAgent.initiateListingAgreement !== 'function') return;
    setSignSubmitting(true);
    setSignError(null);
    try {
      const result = await APIAgent.initiateListingAgreement(signingHouseId, {
        consent_version: agreementPreview.consent_version,
        doc_sha256: agreementPreview.template_sha256,
      });
      const signingUrl = result?.signing_url;
      if (!signingUrl) {
        throw new Error('DocuSign did not return a signing link. Please try again.');
      }
      setSignedAnnouncement('Redirecting you to DocuSign to finish signing…');
      window.location.assign(signingUrl);
    } catch (err) {
      console.error('Resume listing agreement signing failed:', err);
      setSignError(
        err.message || "We couldn't reopen your signing session. Please try again.",
      );
      setSignSubmitting(false);
    }
  };

  const handleSubmit = async (publishNow) => {
    setSubmitting(true);
    setError(null);
    setPublishGateError(null);
    setPublishRefusal(null);
    if (publishNow) setShowRoomErrors(true);
    try {
      const house = await persistListing(publishNow);

      setSuccess(
        publishNow
          ? 'active'
          : isEditMode
          ? editHouse.listing_status === 'draft' ? 'draft-updated' : 'updated'
          : 'draft',
      );
      if (onListingCreated) onListingCreated(house);
      if (onListingSaved) onListingSaved(house);
    } catch (err) {
      // The seller chose to keep their signed report; nothing was saved.
      if (err?.cancelled) return;
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} listing:`, err);
      // Paperwork refusals (§8.2) carry reviewed copy for the listing's state:
      // show the server's message as-is.
      const paperworkCode = paperworkErrorCode(err);
      if (paperworkCode) {
        // A lock (category, basis or state) prefers the server's message, then
        // legal's copy for the lock (on the market / under contract).
        const message =
          paperworkCode === PAPERWORK_CATEGORY_LOCKED
            ? paperworkLockMessage(err, editHouse?.state || form.state)
            : refusalMessage(err) || err.message;
        if (
          paperworkCode === PAPERWORK_BLOCKED ||
          paperworkCode === PAPERWORK_CATEGORY_REQUIRED ||
          isStateLock(err)
        ) {
          // Fixed on Property Details, where the questions and the state are.
          setPaperworkError(message);
          setStep(propertyStepIndex);
        } else if (publishNow) {
          setPublishRefusal(message);
        } else {
          setPaperworkError(message);
        }
        return;
      }
      // A status move refused because the listing is under contract. Updates
      // carry no status, so the only move this form makes is publishing.
      if (isListingUnderContract(err)) {
        setPaperworkError(
          listingUnderContractMessage(err, editHouse?.state || form.state, publishNow ? 'active' : undefined),
        );
        return;
      }
      const refusal = publishNow ? publishRefusalCode(err) : null;
      if (refusal === CONDITION_REPORT_REQUIRED) {
        // The server's message names the right report and basis (§3.7);
        // otherwise build legal's publish-gate line from its title.
        const detail = err.detail || {};
        const title = detail.disclosure_title || disclosureTitle;
        const basis = detail.disclosure_basis === DISCLOSURE_BASIS_VOLUNTARY ? 'voluntary' : 'statutory';
        // Sent to DocuSign but not signed: say so rather than "complete it".
        const signing = isDisclosureSigning(detail.disclosure_status) || wiDisclosureSigning;
        setPublishRefusal(
          refusalMessage(err) ||
            (signing
              ? disclosureSigningText(title)
              : copyText(`WI.disclosure.publish_gate.${basis}`, { title })),
        );
        setDisclosureStatusNonce((n) => n + 1);
        return;
      }
      if (refusal === PROPERTY_FEATURES_INCOMPLETE) {
        // The wizard checks the same rule before publishing, so this is a
        // stale form or a rule change; the server's message says how many.
        setPublishRefusal(refusalMessage(err) || PROPERTY_FEATURES_INCOMPLETE_FALLBACK);
        return;
      }
      if (refusal === REQUIRED_UPLOAD_MISSING) {
        const code = err.detail?.upload_code;
        setPublishRefusal(
          refusalMessage(err) ||
            REQUIRED_UPLOAD_COPY[code]?.missing ||
            REQUIRED_UPLOAD_COPY[UPLOAD_WI_CONDO_RECR_ADDENDUM].missing,
        );
        if (code) {
          // A file that is stored but not (currently) attested stays uploaded;
          // it needs the seller's signature, not a missing file.
          const unattested = (err.detail?.unattested_uploads || []).includes(code);
          setSessionUploads((prev) => ({
            ...prev,
            [code]: { ...(prev[code] || {}), ...(unattested ? { attested: false } : { uploaded: false }) },
          }));
        }
        return;
      }
      // Full Service payment gate (billing-on only): open the Stripe charge modal
      // instead of an error. The listing was persisted as a draft above, so the
      // seller can pay and go live (or retry later from My Listings). Inert when
      // billing is disabled — the 402 never fires.
      if (publishNow && isFullServicePaymentRequired(err)) {
        setChargeOpen(true);
        return;
      }
      // Backend may surface the agreement gate as a 409 race condition.
      const isAgreementGate = publishNow && isListingAgreementRequired(err);
      if (isAgreementGate) {
        setPublishGateError(
          "Can't publish — listing agreement signature required.",
        );
      } else if (isConditionReportSigning(err)) {
        // The report out for DocuSign signature names the old address. Show
        // the server's reason beside the address fields; the seller's other
        // edits stay in the form.
        showAddressSigningRefusal(err);
      } else if (isAddressChangeRequiresWithdrawal(err)) {
        // The UI locks the fields on live listings, so this is a race (status
        // changed elsewhere) — explain the rule rather than show a raw 409.
        setError(ADDRESS_LOCKED_MESSAGE);
      } else if (publishNow && isOwnershipVerificationRequired(err)) {
        setPublishGateError(
          "Can't publish — the review of your ownership documents isn't complete yet.",
        );
        // The server knows best; refresh so the review card and gate agree.
        loadOwnershipStatus(publishHouseIdRef.current || signingHouseId || editHouse?.id);
      } else {
        setError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} listing`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // After a successful Full Service charge, finalize the publish (status →
  // active). The listing was already persisted as a draft, so this just flips the
  // status through the now-paid gate.
  const handleChargeSucceeded = useCallback(async () => {
    setChargeOpen(false);
    const houseId = publishHouseIdRef.current || signingHouseId || editHouse?.id;
    if (!houseId) return;
    setSubmitting(true);
    try {
      const updated = await APIAgent.updateListingStatus(houseId, 'active');
      setSuccess('active');
      if (onListingCreated) onListingCreated(updated);
      if (onListingSaved) onListingSaved(updated);
    } catch (err) {
      // Webhook may still be stamping the paid flag; surface a soft retry hint.
      console.error('Publish after charge failed:', err);
      setError(
        err?.message ||
          "Payment went through — we couldn't publish just yet. Try Publish again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  }, [signingHouseId, editHouse, onListingCreated, onListingSaved]);

  // Auto-dismiss the publish-gate toast after 5s.
  useEffect(() => {
    if (!publishGateError) return;
    const id = setTimeout(() => setPublishGateError(null), 5000);
    return () => clearTimeout(id);
  }, [publishGateError]);

  if (success) {
    return (
      <div className="listing-form__success">
        <div className="listing-form__success-icon">
          {success === 'active' ? '🎉' : success === 'updated' ? '✅' : success === 'draft-updated' ? '📝' : '📝'}
        </div>
        <h3>
          {success === 'active'
            ? 'Listing Published!'
            : success === 'updated'
            ? 'Listing Updated!'
            : success === 'draft-updated'
            ? 'Draft Updated!'
            : 'Draft Saved!'}
        </h3>
        <p>
          {success === 'active'
            ? 'Your property is now live and visible to buyers.'
            : success === 'updated'
            ? 'Your changes have been saved and your listing is still live.'
            : success === 'draft-updated'
            ? 'Your changes have been saved. You can publish it anytime.'
            : 'Your listing has been saved as a draft. You can publish it anytime.'}
        </p>
        <div className="listing-form__success-actions">
          <button className="listing-form__btn listing-form__btn--primary" onClick={onCancel}>
            View My Listings
          </button>
        </div>
      </div>
    );
  }

  // Combined preview URLs for the review step: existing photos first, then
  // newly added ones.
  const allPreviews = [
    ...existingImageUrls.map((url) => APIAgent.getImageUrl(url)),
    ...newPhotos.map((p) => p.preview),
  ];

  return (
    <div className="listing-form">
      {/* Step indicator */}
      <div className="listing-form__steps">
        {STEPS.map((label, i) => (
          <div
            key={label}
            className={`listing-form__step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
          >
            <div className="listing-form__step-dot">
              {i < step ? '✓' : i + 1}
            </div>
            <span className="listing-form__step-label">{label}</span>
          </div>
        ))}
      </div>

      {error && <div className="listing-form__error">{error}</div>}
      {paperworkError && (
        <div className="listing-form__error" role="alert" data-testid="paperwork-error">
          {paperworkError}
        </div>
      )}

      {/* Step 0: Property Details */}
      {step === propertyStepIndex && (
        <div className="listing-form__section">
          <h3 className="listing-form__section-title">Where is your property?</h3>
          {addressLocked && (
            <p
              id="listing-address-locked-hint"
              className="listing-form__address-locked"
              data-testid="address-locked-hint"
            >
              {ADDRESS_LOCKED_MESSAGE}
            </p>
          )}
          {addressSigningLocked && (
            <p
              id="listing-address-signing-hint"
              className="listing-form__address-locked"
              data-testid="address-signing-hint"
            >
              {ADDRESS_SIGNING_LOCKED_MESSAGE}
            </p>
          )}
          {addressSigningError && (
            <p
              id="listing-address-signing-error"
              className="listing-form__error"
              role="alert"
              data-testid="address-signing-error"
            >
              {addressSigningError}
            </p>
          )}
          <div className="listing-form__grid">
            <div className="listing-form__field listing-form__field--full">
              <label className="listing-form__label">Street Address *</label>
              <input
                className="listing-form__input"
                type="text"
                value={form.address}
                onChange={(e) => set('address', e.target.value)}
                placeholder="123 Main Street"
                required
                disabled={addressFieldsDisabled}
                aria-describedby={addressDescribedBy}
              />
            </div>
            <div className="listing-form__field">
              <label className="listing-form__label">City *</label>
              <input
                className="listing-form__input"
                type="text"
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                placeholder="Austin"
                required
                disabled={addressFieldsDisabled}
                aria-describedby={addressDescribedBy}
              />
            </div>
            <div className="listing-form__field listing-form__field--narrow">
              <label className="listing-form__label">State *</label>
              <input
                className="listing-form__input"
                type="text"
                value={form.state}
                onChange={(e) => set('state', e.target.value.toUpperCase())}
                placeholder="TX"
                maxLength={2}
                required
                disabled={addressFieldsDisabled}
                aria-describedby={addressDescribedBy}
              />
            </div>
            <div className="listing-form__field">
              <label className="listing-form__label">Zip Code *</label>
              <input
                className="listing-form__input"
                type="text"
                value={form.zip_code}
                onChange={(e) => set('zip_code', e.target.value)}
                placeholder="78701"
                maxLength={10}
                required
                disabled={addressFieldsDisabled}
                aria-describedby={addressDescribedBy}
              />
            </div>
          </div>

          {/* The property type picks the paperwork, so it is a visible,
              required block (in Wisconsin), not a field in a collapsed
              section. */}
          <PropertyTypeFields
            profile={form.mls_profile}
            result={paperworkResult}
            stateCode={form.state}
            required={isWI}
            onAnswer={handlePaperworkAnswer}
            notAvailable={paperworkNotAvailable}
          />

          {/* Bedrooms, living area, baths and rooms describe a home; vacant
              land has none of them (QA 2026-10-16). */}
          {!isVacantLand && (
          <>
          <h3 className="listing-form__section-title" style={{ marginTop: 28 }}>Property Details</h3>
          <div className="listing-form__grid">
            <div className="listing-form__field">
              <label className="listing-form__label">Bedrooms</label>
              <input
                className="listing-form__input"
                type="number"
                inputMode="numeric"
                value={form.bedrooms}
                onChange={(e) => set('bedrooms', e.target.value)}
                placeholder="3"
                min="0"
                max="50"
              />
            </div>
            <div className="listing-form__field">
              <label className="listing-form__label">Square Feet</label>
              <input
                className="listing-form__input"
                type="number"
                inputMode="numeric"
                value={form.square_feet}
                onChange={(e) => set('square_feet', e.target.value)}
                placeholder="1800"
                min="1"
              />
              <p className="listing-form__hint" style={{ margin: '4px 0 0' }}>
                {MLS_DATA_DISCLAIMER}
              </p>
            </div>
          </div>

          {/* Bath breakdown — replaces the legacy single Bathrooms input. */}
          <BathBreakdownEditor
            value={{
              full: form.full_baths || 0,
              threeQuarter: form.three_quarter_baths || 0,
              half: form.half_baths || 0,
            }}
            wasEstimated={bathWasEstimated}
            confirmedZero={form.bath_zero_confirmed}
            onChange={(next) => {
              setBathWasEstimated(false);
              setForm((prev) => ({
                ...prev,
                full_baths: next.full,
                three_quarter_baths: next.threeQuarter,
                half_baths: next.half,
                // Touching the steppers clears the "needs to confirm zero"
                // ack — they'll only see it again if all three return to 0.
                bath_zero_confirmed:
                  next.full + next.threeQuarter + next.half === 0
                    ? prev.bath_zero_confirmed
                    : false,
              }));
            }}
            onConfirmedZeroChange={(v) => set('bath_zero_confirmed', v)}
          />

          {/* Rooms & dimensions — adds structured room inventory. Autosave
              targets the existing draft endpoint via persistListing(false). */}
          <RoomsEditor
            ref={roomsEditorRef}
            rooms={form.rooms}
            onChange={(nextRooms) => setForm((prev) => ({ ...prev, rooms: nextRooms }))}
            onAutosave={handleRoomsAutosave}
            hadLegacyTotalRooms={Boolean(
              !isEditMode || !editHouse?.rooms?.length
                ? form.mls_profile?.total_rooms
                : false,
            )}
            legacyTotalRoomsCount={
              Number(form.mls_profile?.total_rooms) || 0
            }
            showErrors={showRoomErrors}
            disclaimer={MLS_DATA_DISCLAIMER}
          />
          </>
          )}

          <h3 className="listing-form__section-title" style={{ marginTop: 28 }}>Property Features</h3>
          <p className="listing-form__hint">
            Tell buyers about your home. Every field marked * is required —
            pick &ldquo;None&rdquo; where nothing applies.
          </p>
          <div className="listing-form__mls-sections">
            {MLS_SECTIONS.map((section) => (
              <details className="listing-form__mls-section" key={section.key || section.title}>
                <summary className="listing-form__mls-summary">
                  <span className="listing-form__mls-summary-icon">{section.icon}</span>
                  {section.title}
                  {(() => {
                    const left = missingFeatures.filter((f) => f.section === section.key).length;
                    return left > 0 ? (
                      <span className="listing-form__mls-summary-badge">{left} to fill in</span>
                    ) : null;
                  })()}
                </summary>
                <div className="listing-form__grid listing-form__mls-grid">
                  {section.fields.map((field) => renderMlsField(field))}
                </div>
              </details>
            ))}
          </div>

          {/* Price + AI estimate close out Property Details, after every home
              fact above: the estimate prices whatever is filled in when it is
              clicked, so offering it above the baths, rooms and features priced
              a house the seller hadn't finished describing. Still a button on
              the screen where the price goes, not a wizard step (QA T-31). */}
          <h3 className="listing-form__section-title" style={{ marginTop: 28 }}>
            {isVacantLand ? 'Price your land' : 'Price your home'}
          </h3>
          <div className="listing-form__grid">
            <div className="listing-form__field">
              <label className="listing-form__label" htmlFor="listing-price-input">
                Listing Price ($) *
              </label>
              <input
                id="listing-price-input"
                ref={priceInputRef}
                className="listing-form__input"
                type="number"
                inputMode="numeric"
                value={form.price}
                onChange={(e) => set('price', e.target.value)}
                placeholder="450000"
                min="0"
                required
                aria-describedby="listing-price-estimate-help"
              />
              <p className="listing-form__hint" id="listing-price-estimate-help" style={{ margin: '4px 0 0' }}>
                No number in mind? Get an AI estimate below — you don't need a price first.
              </p>
            </div>
          </div>

          {/* The request never sends `price` (see PriceEstimateRequest in
              agent_endpoints.py), so running it before the seller has picked a
              number is neither blocked nor circular. */}
          <div className="listing-form__estimate-section" data-testid="price-estimate-section">
            <h4 className="listing-form__estimate-heading">Not sure what to ask for it?</h4>
            {/* Says what the estimate is built from: no listing or sales data is
                fetched, the model prices from patterns learned in training
                (wording per legal review — no "value", no market-data claim). */}
            <p className="listing-form__hint">
              Our AI suggests a price range from the details you entered above and
              general pricing patterns it learned during training, which may be out
              of date. It doesn&apos;t look up current listings or recent sales, so
              treat it only as a rough starting point. Optional — you can set your
              own price at any time.
            </p>

            {canGetEstimate && missingEstimateDetails.length > 0 && (
              <p
                className="listing-form__hint listing-form__estimate-gaps"
                data-testid="price-estimate-gaps"
              >
                For a sharper estimate, add {joinWithAnd(missingEstimateDetails)} above.
              </p>
            )}

            {canGetEstimate && priceEstimateStale && !priceEstimateLoading && (
              <p
                className="listing-form__estimate-stale"
                id="price-estimate-stale-note"
                data-testid="price-estimate-stale"
              >
                You&apos;ve changed property details since this estimate. Update it so
                the range reflects them.
              </p>
            )}

            <button
              type="button"
              className="listing-form__estimate-btn"
              onClick={handleGetPriceEstimate}
              disabled={!canGetEstimate || priceEstimateLoading}
              aria-busy={priceEstimateLoading}
              aria-describedby={
                showEstimateRequirements
                  ? 'price-estimate-requirements'
                  : priceEstimateStale
                  ? 'price-estimate-stale-note'
                  : undefined
              }
              data-testid="get-price-estimate-btn"
            >
              <span aria-hidden="true">✨</span>
              {priceEstimateLoading
                ? 'Estimating…'
                : priceEstimateStale
                ? 'Update AI Price Estimate'
                : 'Get AI Price Estimate'}
            </button>

            {showEstimateRequirements && (
              <p
                className="listing-form__hint"
                id="price-estimate-requirements"
                style={{ marginTop: 6 }}
              >
                {isVacantLand
                  ? 'Fill in the address above plus the lot size (under Exterior & Lot) to enable the estimate.'
                  : 'Fill in the address above plus at least one of bedrooms, baths, or square feet to enable the estimate.'}
              </p>
            )}

            {/* Visually-hidden a11y live region: announces a landed estimate and
                the price accepted from it. */}
            <div
              className="listing-form__visually-hidden"
              aria-live="polite"
              aria-atomic="true"
              data-testid="price-estimate-announcer"
            >
              {priceEstimateAnnouncement}
            </div>

            <PriceEstimateCard
              estimate={priceEstimate?.estimate}
              confidence={priceEstimate?.confidence}
              factors={priceEstimate?.factors}
              comparables={priceEstimate?.comparables}
              disclaimer={priceEstimate?.disclaimer}
              loading={priceEstimateLoading}
              error={priceEstimateError}
              onUsePrice={handleUsePriceEstimate}
              onRetry={priceEstimateRetryable ? handleGetPriceEstimate : undefined}
            />
          </div>
        </div>
      )}

      {/* Listing Fee (billing on only) */}
      {step === listingFeeStepIndex && (
        <div className="listing-form__section">
          <ListingFeeStep
            houseId={draftReadyForStep ? signingHouseId : null}
            listingAddress={form.address}
            listPrice={form.price}
            fee={listingFee}
            onFeeChange={handleListingFeeChange}
          />
        </div>
      )}

      {/* Confirm Ownership */}
      {step === ownershipStepIndex && (
        <div className="listing-form__section">
          <OwnershipVerificationStep
            houseId={draftReadyForStep ? signingHouseId : null}
            status={ownershipStatus}
            loading={ownershipLoading}
            loadError={ownershipError}
            onRefresh={() => loadOwnershipStatus(signingHouseId)}
            onStatusChange={setOwnershipStatus}
            grandfathered={ownershipStatus?.verification_required === false}
            previouslyPublished={previouslyPublished}
          />
        </div>
      )}

      {/* Description & Photos */}
      {step === descriptionStepIndex && (
        <div className="listing-form__section">
          <h3 className="listing-form__section-title">Describe your property</h3>
          <div className="listing-form__field listing-form__field--full">
            <label className="listing-form__label">Description * (min 10 characters)</label>
            <textarea
              className="listing-form__textarea"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Describe your property's best features, recent upgrades, neighborhood highlights..."
              rows={5}
            />
            <span className="listing-form__char-count">{form.description.length} / 2000</span>
          </div>

          <h3 className="listing-form__section-title" style={{ marginTop: 28 }}>Photos</h3>
          <p className="listing-form__hint">
            Add photos to help buyers visualize your home. Drag & drop or tap to add. You can add more later.
          </p>
          <div
            className={`listing-form__photos ${dragActive ? 'listing-form__photos--drag-active' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {existingImageUrls.map((url, idx) => (
              <div className="listing-form__photo" key={`existing-${idx}`}>
                <img src={APIAgent.getImageUrl(url)} alt={`Property ${idx + 1}`} />
                <button
                  className="listing-form__photo-remove"
                  onClick={() => handleExistingImageRemove(idx)}
                  aria-label="Remove photo"
                >
                  ×
                </button>
              </div>
            ))}
            {newPhotos.map((photo) => (
              <div className="listing-form__photo" key={`new-${photo.id}`}>
                <img src={photo.preview} alt="Property" />
                {photo.processing && (
                  <div className="listing-form__photo-processing" aria-label="Processing photo">
                    <span className="listing-form__photo-spinner" />
                  </div>
                )}
                <button
                  className="listing-form__photo-remove"
                  onClick={() => handleNewPhotoRemove(photo.id)}
                  aria-label="Remove photo"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              className="listing-form__photo-add"
              type="button"
              onClick={() => fileInputRef.current?.click()}
            >
              <span className="listing-form__photo-add-icon">＋</span>
              <span>Add Photo</span>
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageAdd}
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              hidden
            />
          </div>
        </div>
      )}

      {/* WI Seller Disclosure Step (only for Wisconsin properties).
          The seller completes the actual Real Estate Condition Report here.
          Previously this step collected only an acknowledgement that they
          would complete one later, and the report itself lived post-acceptance
          in the negotiation workspace. */}
      {isWI && step === wiStepIndex && (
        <div className="listing-form__section">
          {disclosureVoided && (
            <div
              className="listing-form__sign-resign-banner"
              role="status"
              data-testid="disclosure-voided-notice"
            >
              <span className="listing-form__wi-disclosure-icon" aria-hidden="true">⚠</span>
              <div>
                <p>{copyText('WI.paperwork.change.voided_notice', { title: disclosureTitle })}</p>
              </div>
            </div>
          )}
          {signingHouseId && draftReadyForStep && !disclosureNeedsResave ? (
            // Keyed by the saved paperwork so a new category or basis loads
            // its own form from scratch (design §9.4). draftReadyForStep: this
            // visit's entry save has finished, so the form never reads a stale draft.
            <RecrDisclosureForm
              key={savedPaperworkKey}
              houseId={signingHouseId}
              expectedTitle={disclosureTitle}
              lastSigningError={disclosureSigningError}
              onStatusChange={(status) => {
                setWiDisclosureStatus(status);
                // A report signed (or sent for signature) since the
                // paperwork change replaces the voided one.
                if (status?.status && status.status !== 'draft') setDisclosureVoided(false);
              }}
            />
          ) : (
            <p className="listing-form__wi-disclosure-warning">
              Saving your listing draft so your {disclosureTitle} can be attached…
            </p>
          )}
        </div>
      )}

      {/* Sign Listing Agreement Step */}
      {step === signStepIndex && (
        <div className="listing-form__section">
          <h3 className="listing-form__section-title">Sign Listing Agreement</h3>

          {/* Visually-hidden a11y live region announces a successful sign */}
          <div
            className="listing-form__visually-hidden"
            aria-live="polite"
            aria-atomic="true"
            data-testid="sign-agreement-announcer"
          >
            {signedAnnouncement}
          </div>

          {/* ── Pending state: envelope out for signature, not yet completed.
              This covers BOTH "seller abandoned the ceremony" and "seller just
              signed and reconcile/webhook is still landing" — we can't tell them
              apart from the status alone, so the copy must not claim a signature
              exists. Resume re-enters the same envelope; Refresh re-runs
              reconcile for the just-signed case. When the active template has
              moved on we can't resume (see `agreementPendingStale`), so the full
              pre-sign gate renders below instead. ── */}
          {agreementPending && (
            <div
              className="listing-form__sign-resign-banner"
              role="status"
              data-testid="sign-pending-banner"
            >
              <span className="listing-form__wi-disclosure-icon" aria-hidden="true">⏳</span>
              <div>
                {agreementPendingStale ? (
                  <>
                    <strong>The agreement changed while you were signing.</strong>
                    <p>
                      A newer version of the listing agreement is now in effect, so
                      the signing session you started is out of date. Review the
                      current version below and sign it to publish.
                    </p>
                  </>
                ) : (
                  <>
                    <strong>Your agreement is waiting for your signature.</strong>
                    <p>
                      You started signing with DocuSign but haven&apos;t finished.
                      Resume to pick up where you left off. If you just signed,
                      choose Refresh — we&apos;re still confirming it with DocuSign.
                    </p>
                  </>
                )}
                <div className="listing-form__sign-pending-actions">
                  {canResumeSigning && (
                    <button
                      type="button"
                      className="listing-form__btn listing-form__btn--primary"
                      onClick={handleResumeSigning}
                      disabled={signSubmitting || !agreementPreview}
                      data-testid="sign-pending-resume-btn"
                    >
                      {signSubmitting ? 'Opening DocuSign…' : 'Resume signing'}
                    </button>
                  )}
                  {/* The envelope they resume — print it before finishing. A
                      stale envelope gets the full pre-sign UI (and its own
                      print button) below instead. */}
                  {canResumeSigning && (
                    <ListingAgreementPreviewButton
                      houseId={signingHouseId}
                      buttonClassName="listing-form__btn listing-form__btn--secondary"
                      hint={null}
                    />
                  )}
                  <button
                    type="button"
                    className="listing-form__btn listing-form__btn--secondary"
                    onClick={handleRefreshAgreementStatus}
                    disabled={refreshingStatus}
                    data-testid="sign-pending-refresh-btn"
                  >
                    {refreshingStatus ? 'Checking…' : 'Refresh status'}
                  </button>
                </div>
                {signError && (
                  <p className="listing-form__sign-error" role="alert">
                    {signError}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── Voided state: last envelope was declined/cancelled. The
              pre-sign UI below renders so the seller can sign again; this
              banner explains why they're back at the start. ── */}
          {agreementVoided && (
            <div
              className="listing-form__sign-resign-banner"
              role="status"
              data-testid="sign-voided-banner"
            >
              <span className="listing-form__wi-disclosure-icon" aria-hidden="true">⚠</span>
              <div>
                <strong>Signing was not completed.</strong>
                <p>
                  The previous signing session was declined or cancelled. Review
                  the agreement and sign again to publish.
                </p>
              </div>
            </div>
          )}

          {/* ── Pre-sign state ─────────────────────────────────────────
              Also renders for a pending-but-stale envelope: that seller must
              re-read and re-consent to the current version, since `/initiate`
              will void the out-of-date envelope and mint a fresh one. ── */}
          {(!agreementPending || agreementPendingStale) &&
            (!hasSignedAgreement || signedVersionStale) && (
            <div className="listing-form__sign-agreement">
              {agreementError && !agreementPreview ? (
                <div
                  className="listing-form__agreement-error"
                  role="alert"
                  data-testid="agreement-load-error"
                >
                  <span className="listing-form__wi-disclosure-icon" aria-hidden="true">⚠</span>
                  <div>
                    <strong>Couldn't load the agreement.</strong>
                    <p>{agreementError}</p>
                    <button
                      type="button"
                      className="listing-form__btn listing-form__btn--secondary"
                      onClick={handleRetryLoadAgreement}
                      disabled={agreementLoading}
                      data-testid="retry-load-agreement-btn"
                    >
                      {agreementLoading ? 'Retrying…' : 'Retry'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className="listing-form__agreement-preview"
                    ref={previewScrollContainerRef}
                    tabIndex={0}
                    aria-label="Listing agreement preview — scroll to the bottom to enable signing"
                    data-testid="agreement-preview"
                  >
                    <div className="listing-form__agreement-preview-header">
                      <span className="listing-form__agreement-preview-title">
                        Limited-Service Platform / Marketing Services Agreement
                      </span>
                      {agreementPreview?.template_version != null && (
                        <span className="listing-form__agreement-preview-version">
                          v{agreementPreview.template_version}
                        </span>
                      )}
                    </div>
                    <div className="listing-form__agreement-preview-body">
                      {agreementLoading && !agreementPreview && (
                        <p className="listing-form__hint">Loading agreement…</p>
                      )}
                      {agreementPreview?.rendered_html && (
                        <div
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{ __html: agreementPreview.rendered_html }}
                        />
                      )}
                      <div
                        ref={previewBottomSentinelRef}
                        className="listing-form__agreement-preview-sentinel"
                        aria-hidden="true"
                        data-testid="agreement-preview-bottom"
                      />
                    </div>
                  </div>

                  {/* QA T-45: a printable copy before signing, not only after. */}
                  <ListingAgreementPreviewButton
                    houseId={signingHouseId}
                    buttonClassName="listing-form__btn listing-form__btn--secondary"
                  />

                  <div className="listing-form__wi-disclosure-notice listing-form__sign-disclaimers">
                    <span className="listing-form__wi-disclosure-icon" aria-hidden="true">⚖️</span>
                    <div>
                      <strong>Before you sign — please read</strong>
                      <ul className="listing-form__sign-disclaimer-list">
                        {(agreementPreview?.disclaimers?.length
                          ? agreementPreview.disclaimers
                          : FALLBACK_DISCLAIMERS
                        ).map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="listing-form__sign-affirm">
                    <h4 className="listing-form__sign-affirm-title">Sign your agreement</h4>

                    <p className="listing-form__hint listing-form__sign-docusign-note">
                      You&apos;ll be securely redirected to DocuSign to review and
                      apply your signature. After signing, you&apos;ll return here
                      to publish your listing.
                    </p>

                    <label className="listing-form__wi-disclosure-checkbox listing-form__sign-consent">
                      <input
                        type="checkbox"
                        checked={consentChecked}
                        onChange={(e) => setConsentChecked(e.target.checked)}
                        aria-required="true"
                        data-testid="sign-consent-checkbox"
                      />
                      <span>{ESIGN_CONSENT_COPY}</span>
                    </label>

                    {signError && (
                      <p
                        className="listing-form__sign-error"
                        role="alert"
                        data-testid="sign-error-message"
                      >
                        {signError}
                      </p>
                    )}

                    <button
                      type="button"
                      className="listing-form__btn listing-form__btn--primary listing-form__sign-submit"
                      onClick={handleSignAgreement}
                      aria-disabled={!canSign}
                      disabled={!canSign}
                      title={signDisabledTooltip}
                      data-testid="sign-agreement-btn"
                    >
                      {signSubmitting ? 'Redirecting to DocuSign…' : 'Sign with DocuSign'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Post-sign state ────────────────────────────────────── */}
          {hasSignedAgreement && !signedVersionStale && (
            <div
              className="listing-form__sign-confirm"
              role="status"
              tabIndex={-1}
              ref={signedConfirmRef}
              data-testid="sign-confirm-card"
            >
              <div className="listing-form__sign-confirm-header">
                <span className="listing-form__sign-confirm-icon" aria-hidden="true">✓</span>
                <strong>Signed</strong>
              </div>
              <dl className="listing-form__sign-confirm-meta">
                <div>
                  <dt>Signed by</dt>
                  <dd data-testid="signed-by-name">
                    {signedAgreement.signer_name || '—'}
                  </dd>
                </div>
                <div>
                  <dt>When</dt>
                  <dd data-testid="signed-at">{formatUtcDate(signedAgreement.signed_at)}</dd>
                </div>
                <div>
                  <dt>Template</dt>
                  <dd>v{signedAgreement.template_version}</dd>
                </div>
                {signedAgreement.document_sha256 && (
                  <div>
                    <dt>Doc hash</dt>
                    <dd>
                      <code>{truncateHash(signedAgreement.document_sha256)}</code>
                    </dd>
                  </div>
                )}
              </dl>
              <div className="listing-form__sign-confirm-actions">
                {signedAgreement.download_url && (
                  <a
                    className="listing-form__btn listing-form__btn--secondary"
                    href={resolveDownloadUrl(signedAgreement.download_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="signed-download-link"
                  >
                    Download signed copy (PDF)
                  </a>
                )}
                <button
                  type="button"
                  className="listing-form__btn listing-form__btn--ghost"
                  onClick={() => setShowSignedAgreement((v) => !v)}
                  data-testid="signed-show-agreement-toggle"
                >
                  {showSignedAgreement ? 'Hide agreement' : 'Show agreement'}
                </button>
              </div>
              {showSignedAgreement && agreementPreview?.rendered_html && (
                <div
                  className="listing-form__agreement-preview listing-form__agreement-preview--signed"
                  tabIndex={0}
                  aria-label="Signed listing agreement"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: agreementPreview.rendered_html }}
                />
              )}
            </div>
          )}

          {/* Re-sign affordance: post-sign card with a stale template surfaces a
              banner that triggers the resign flow. Visible above the pre-sign UI
              we re-render once handleResignLatest clears the signed flag. */}
          {hasSignedAgreement && signedVersionStale && (
            <div
              className="listing-form__sign-resign-banner"
              role="status"
              data-testid="sign-resign-banner"
            >
              <span className="listing-form__wi-disclosure-icon" aria-hidden="true">⚠</span>
              <div>
                <strong>
                  A newer agreement version (v{signedAgreement.current_template_version}) is
                  available.
                </strong>
                <p>You signed v{signedAgreement.template_version}. Re-sign to publish.</p>
                <button
                  type="button"
                  className="listing-form__btn listing-form__btn--primary"
                  onClick={handleResignLatest}
                  data-testid="resign-latest-btn"
                >
                  Re-sign latest version
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Review & Publish Step */}
      {step === reviewStepIndex && (
        <div className="listing-form__section">
          <h3 className="listing-form__section-title">Review Your Listing</h3>
          <div className="listing-form__review">
            <div className="listing-form__review-card">
              {allPreviews.length > 0 && (
                <div className="listing-form__review-hero">
                  <img src={allPreviews[0]} alt="Primary property" />
                  {allPreviews.length > 1 && (
                    <span className="listing-form__review-photo-count">
                      +{allPreviews.length - 1} more
                    </span>
                  )}
                </div>
              )}
              <div className="listing-form__review-body">
                <h4 className="listing-form__review-address">{form.address}</h4>
                <p className="listing-form__review-location">
                  {form.city}, {form.state} {form.zip_code}
                </p>
                {form.price && (
                  <p className="listing-form__review-price">
                    ${parseFloat(form.price).toLocaleString()}
                  </p>
                )}
                <div className="listing-form__review-specs">
                  {form.bedrooms && <span>{form.bedrooms} bd</span>}
                  {bathTotal > 0 && (
                    <span data-testid="review-spec-baths">
                      {bathTotal} ba
                      {bathMlsDecimal !== bathTotal
                        ? ` (${bathMlsDecimal.toFixed(2)} MLS)`
                        : ''}
                    </span>
                  )}
                  {form.square_feet && <span>{parseFloat(form.square_feet).toLocaleString()} sqft</span>}
                  {(form.rooms || []).length > 0 && (
                    <span>{form.rooms.length} rooms</span>
                  )}
                </div>
                <p className="listing-form__review-desc">{form.description}</p>
              </div>
            </div>

            {/* Ownership verification status */}
            {(ownershipRequired || ownershipStatus?.status) && (
              <div
                className={`listing-form__review-ownership listing-form__review-ownership--${
                  ownershipVerified ? 'verified' : ownershipStatus?.status === 'rejected' ? 'blocked' : 'pending'
                }`}
                data-testid="review-ownership-status"
              >
                <span className="listing-form__review-ownership-icon" aria-hidden="true">
                  {ownershipVerified ? '✓' : ownershipStatus?.status === 'rejected' ? '!' : '…'}
                </span>
                <div>
                  <strong>
                    Ownership:{' '}
                    {ownershipLoading && !ownershipStatus
                      ? 'checking…'
                      : OWNERSHIP_STATUS_LABELS[ownershipStatus?.status || 'not_started']}
                  </strong>
                  {ownershipRequired && !ownershipVerified && (
                    <p>
                      {ownershipStatus?.status === 'needs_review'
                        ? 'A cutthe6.com staff member is reviewing your documents. You can publish once the review is complete.'
                        : ownershipStatus?.status === 'rejected'
                        ? "We couldn't confirm your ownership or authority, so this listing can't be published."
                        : 'Confirm your ownership or authority before publishing.'}{' '}
                      {ownershipStatus?.status !== 'rejected' && (
                        <button
                          type="button"
                          className="listing-form__link-btn"
                          onClick={() => setStep(ownershipStepIndex)}
                          data-testid="review-ownership-go"
                        >
                          {ownershipStatus?.status === 'needs_review' ? 'View status' : 'Upload ownership documents'}
                        </button>
                      )}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Listing-agreement signed badge */}
            {hasSignedAgreement && (
              <div
                className="listing-form__wi-disclosure-notice listing-form__review-agreement-badge"
                data-testid="review-agreement-badge"
              >
                <span className="listing-form__wi-disclosure-icon" aria-hidden="true">📄</span>
                <div>
                  <strong>
                    Listing Agreement: signed by{' '}
                    {signedAgreement.signer_name || 'seller'} on{' '}
                    {formatUtcShortDate(signedAgreement.signed_at)} · v
                    {signedAgreement.template_version}
                  </strong>
                  {signedAgreement.download_url && (
                    <p>
                      <a
                        href={resolveDownloadUrl(signedAgreement.download_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="review-download-link"
                      >
                        Download signed copy
                      </a>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* What the publish gate checks, in its order (WI). */}
            {isWI && (
              <PublishChecklist
                items={[
                  {
                    key: 'paperwork',
                    label: 'Property type and paperwork',
                    done: paperworkResult.status === STATUS_OK,
                    detail:
                      paperworkLine(form.state, paperworkResult) ||
                      paperworkBlocker(),
                  },
                  {
                    key: 'disclosure',
                    label: disclosureTitle,
                    done: wiDisclosureCompleted,
                    detail: wiDisclosureCompleted
                      ? null
                      : wiDisclosureSigning
                        ? disclosureSigningText(disclosureTitle)
                        : disclosureVoided
                        ? copyText('WI.paperwork.change.voided_notice', { title: disclosureTitle })
                        : copyText('WI.disclosure.publish_tooltip', { title: disclosureTitle }),
                  },
                  {
                    key: 'agreement',
                    label: 'Listing agreement signed',
                    done: hasSignedAgreement && !signedVersionStale,
                  },
                  // Ownership verification is a publish gate too (#181); left
                  // out when the server says this listing doesn't need it.
                  ...(ownershipRequired
                    ? [
                        {
                          key: 'ownership',
                          label: 'Ownership confirmed',
                          done: ownershipVerified,
                          detail: ownershipBlocker(),
                        },
                      ]
                    : []),
                ]}
                uploads={requiredUploads.map((u) => ({
                  ...u,
                  houseId: signingHouseId || editHouse?.id || null,
                }))}
                onUploaded={handleRequiredUploaded}
              />
            )}
          </div>
          {publishRefusal && (
            <div
              className="listing-form__error"
              role="alert"
              data-testid="publish-refusal"
            >
              {publishRefusal}
            </div>
          )}
        </div>
      )}

      {/* Publish-gate race-condition toast: surfaces when backend 409s with
          code "listing_agreement_required" after the seller bypassed the UI
          gate. Auto-dismisses in 5s. */}
      {publishGateError && (
        <div
          className="listing-form__publish-gate-toast"
          role="alert"
          data-testid="publish-gate-toast"
        >
          {publishGateError}
        </div>
      )}

      {/* Full Service charge modal (PR5). Opens only after a publish attempt hit
          the 402 payment gate (billing enabled). Lazy-loaded so Stripe only loads
          at the publish step. On success it finalizes the draft → active flip. */}
      {chargeOpen && (
        <Suspense fallback={null}>
          <OneTimeChargeModal
            open={chargeOpen}
            houseId={publishHouseIdRef.current || signingHouseId || editHouse?.id}
            listingAddress={form.address}
            listPrice={form.price}
            onClose={() => setChargeOpen(false)}
            onSucceeded={handleChargeSucceeded}
          />
        </Suspense>
      )}

      {/* Navigation */}
      <div className="listing-form__nav">
        {step > 0 ? (
          <button
            className="listing-form__btn listing-form__btn--secondary"
            onClick={() => setStep(step - 1)}
            disabled={submitting}
          >
            ← Back
          </button>
        ) : (
          <button
            className="listing-form__btn listing-form__btn--ghost"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
        )}

        <div className="listing-form__nav-right">
          {step < reviewStepIndex ? (
            <>
              <button
                className="listing-form__btn listing-form__btn--secondary"
                onClick={() => handleSubmit(false)}
                // A hard block is never saved (422), but "not sure" answers
                // still save as a draft while the seller checks.
                disabled={submitting || !isPropertyStepValid() || paperworkBlocked}
                title={paperworkBlocked ? paperworkBlocker() : undefined}
                data-testid="save-draft-btn"
              >
                {submitting ? 'Saving...' : isEditMode && editHouse?.listing_status !== 'draft' ? 'Save Changes' : 'Save as Draft'}
              </button>
              <button
                className="listing-form__btn listing-form__btn--primary"
                onClick={() => setStep(step + 1)}
                aria-disabled={!canAdvance() || submitting}
                disabled={!canAdvance() || submitting}
                title={continueTooltip}
                data-testid="continue-btn"
              >
                Continue →
              </button>
            </>
          ) : (
            <>
              <button
                className="listing-form__btn listing-form__btn--secondary"
                onClick={() => handleSubmit(false)}
                disabled={submitting || paperworkBlocked}
                title={paperworkBlocked ? paperworkBlocker() : undefined}
              >
                {submitting ? 'Saving...' : isEditMode && editHouse?.listing_status !== 'draft' ? 'Save Changes' : 'Save as Draft'}
              </button>
              <button
                className="listing-form__btn listing-form__btn--primary"
                onClick={() => handleSubmit(true)}
                aria-disabled={submitting || !canPublish()}
                disabled={submitting || !canPublish()}
                title={publishDisabledTooltip()}
                data-testid="publish-btn"
              >
                {submitting ? 'Publishing...' : '🚀 Publish Listing'}
              </button>
            </>
          )}
        </div>
      </div>
      {navBlockedReason && (
        // The disabled button's title only shows on hover; say why here too.
        <p className="listing-form__nav-blocked" role="status" data-testid="nav-blocked-reason">
          {navBlockedReason}
        </p>
      )}
    </div>
  );
}
